// Auto-Deck — the executive board rendered when the `auto_insights_board` flag is ON
// (Requirements 5, 3, 4, 14, 16). Deterministic spec comes from the backend (/ai/board); this
// component ONLY renders it — no metric math, no chart-type choice, no formatting decisions
// here (all of that is the server's deterministic engine + design tokens). Colors/format come
// from the mirrored design tokens so the in-app view matches the PPTX/living-link export.
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  BarChart, Bar, LineChart, Line, ComposedChart, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine, Customized,
} from "recharts";
import { RefreshCw, Maximize2, X } from "lucide-react";
import { getFreshToken } from "../api";
import { useBoardActivity } from "./BoardActivityContext";
import { PALETTE, SEMANTIC, ORDINAL, ROLES, KPI_TINT, TABLE_STYLE } from "./designTokens";
import { extractPaletteFromImage } from "./extractPalette";
import TableFilterChips from "./TableFilterChips";
import { chipsForTable, reconcileNoteForVisual, sourceLabelFor, uncalculableNotesForVisual } from "./tableFilters";

const API_URL = (process.env.REACT_APP_API_URL || "").replace(/^["']|["']$/g, "").trim().replace(/\/$/, "");

// The first /ai/board triggers server-side generation and is persisted as it builds; a slow or
// transient first response is NOT a failure. Retry silently (~3s apart) for up to this many
// attempts behind the "Building your board…" state before showing a soft message.
//
// Was 20 (~60s) — the EXACT same window as the backend's own stuck-board guardrail
// (`board._MAX_BOARD_AGE_S = 60.0`). A large template publish (18-19 charts) could hit BOTH
// ceilings within the same couple of seconds of each other: the client gave up and showed
// "Regeneration failed" at almost the same moment the backend was about to force-resolve the
// board to a real terminal state on the very next poll. Bumped so the client comfortably
// outlasts the backend's own guardrail instead of racing it — the board is guaranteed to reach
// a terminal (ready/needs_data) state within 60s server-side, so giving the client a bit more
// runway means it actually SEES that resolution instead of erroring right before it happens.
const MAX_BUILD_RETRIES = 30;   // ~90s at 3s/retry

// ─── Formatting (mirrors design_system format keys; render == export) ───────────────────────
const _MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// REAL BUG, found live-testing the actual KIWI file (the age-bracket Pinned Table, e.g. row
// labels "25-34", "35-44"): `parseFloat` is LENIENT -- it reads only the numeric PREFIX of a
// string and silently stops at the first non-numeric character, so `parseFloat("25-34")` returns
// `25`, not NaN. Every row label rendered as just its first number ("25", "35", "45"...) instead
// of the real range text, because this coercion wrongly treated a text label as a valid number.
// The backend's OWN identical-purpose coercions (`export_xlsx.py._num`, `design_system.
// _to_number`) never had this bug -- both use Python's `float(s)`, which is STRICT and raises on
// any trailing garbage, correctly rejecting "25-34" outright -- so Excel/PPTX export and the
// backend's own numeric/alignment decisions were always correct; only this ONE frontend function
// disagreed, which is exactly why the export looked right but the in-app dashboard didn't. Fixed
// by requiring the WHOLE cleaned string to match a strict numeric pattern (optional sign, digits,
// optional decimal part, optional scientific-notation exponent -- e.g. Excel's own cached
// "4.4999999999999998E-2" cell value, which this function must still accept, see
// `_tableCellDisplay`'s own comment on that exact shape) before accepting it as a number -- never
// a partial-prefix match. A real number still parses correctly; a range-shaped label like
// "25-34" now correctly returns null and falls through to plain text rendering.
const _STRICT_NUMBER_RE = /^[+-]?\d+(\.\d+)?([eE][+-]?\d+)?$/;
function _num(v) {
  if (v === null || v === undefined || v === "") return null;
  const cleaned = String(v).replace(/[$,%\s\u00a0]/g, "");
  if (!_STRICT_NUMBER_RE.test(cleaned)) return null;
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
}
// `decimalsOverride` (optional, roadmap: "the excel use three decimals, so the tables has to
// follow the same precision as the reference table"): a Pinned Table cell's OWN source-file
// numFmt decimal count (`blueprint_render._cell_format_decimals`, e.g. 3 for a `0.000%` CTR
// column whose tiny values would otherwise round away to nothing at the default 1 decimal) —
// omitted (undefined/null) for every existing caller (charts, KPIs, non-table cells), which
// keeps each format's own hardcoded default decimal count EXACTLY as before this existed.
function fmtValue(v, fmt, decimalsOverride) {
  const n = _num(v);
  if (fmt === "date") {
    const m = String(v || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${_MONTHS[+m[2] - 1]} ${+m[3]}, ${m[1]}`;
    return String(v ?? "");
  }
  if (n === null) return String(v ?? "");
  const hasOverride = decimalsOverride !== undefined && decimalsOverride !== null && !isNaN(decimalsOverride);
  // R13.2 — a Blueprint currency field carries a composite "currency:CODE" key (e.g.
  // "currency:CLP"); mirrors design_system.currency_meta so in-app formatting matches the
  // backend/export exactly for a non-USD field, not always a hardcoded "$".
  if (fmt === "currency" || (typeof fmt === "string" && fmt.startsWith("currency:"))) {
    const code = fmt.startsWith("currency:") ? fmt.slice("currency:".length) : null;
    const meta = currencyMeta(code);
    const decimals = hasOverride ? decimalsOverride : meta.decimals;
    const body = n.toLocaleString("en-US", decimals === 0 ? { maximumFractionDigits: 0 } :
      { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    return meta.position === "prefix" ? meta.symbol + body : body + meta.symbol;
  }
  if (fmt === "percent") {   // 0-1 fraction → ×100
    const decimals = hasOverride ? decimalsOverride : 1;
    return (n * 100).toFixed(decimals) + "%";
  }
  if (fmt === "percent_scaled") {   // already 0-100 → no ×100
    const decimals = hasOverride ? decimalsOverride : (n % 1 === 0 ? 0 : 1);
    return n.toFixed(decimals) + "%";
  }
  if (fmt === "ratio") return n.toFixed(hasOverride ? decimalsOverride : 1) + "x";
  if (hasOverride) return n.toLocaleString("en-US",
    { minimumFractionDigits: decimalsOverride, maximumFractionDigits: decimalsOverride });
  return n.toLocaleString();
}
// ISO-4217 symbol/decimals/position (fixed-visual-blueprint R13.2) — mirrors
// backend/app/insights/design_system.py's `_CURRENCY_META` + `currency_meta()` exactly, so
// in-app rendering, PPTX export, and Excel export never disagree on a non-USD field's format.
const _CURRENCY_META = {
  USD: { symbol: "$", decimals: 2, position: "prefix" },
  EUR: { symbol: "€", decimals: 2, position: "prefix" },
  GBP: { symbol: "£", decimals: 2, position: "prefix" },
  JPY: { symbol: "¥", decimals: 0, position: "prefix" },
  CNY: { symbol: "¥", decimals: 2, position: "prefix" },
  CAD: { symbol: "$", decimals: 2, position: "prefix" },
  AUD: { symbol: "$", decimals: 2, position: "prefix" },
  CLP: { symbol: "$", decimals: 0, position: "prefix" },
  MXN: { symbol: "$", decimals: 2, position: "prefix" },
  BRL: { symbol: "R$", decimals: 2, position: "prefix" },
  ARS: { symbol: "$", decimals: 2, position: "prefix" },
  COP: { symbol: "$", decimals: 0, position: "prefix" },
  PEN: { symbol: "S/", decimals: 2, position: "prefix" },
};
function currencyMeta(code) {
  const c = (code || "USD").trim().toUpperCase();
  if (_CURRENCY_META[c]) return _CURRENCY_META[c];
  return { symbol: c, decimals: 2, position: "prefix" };   // uncurated-but-valid code → USD-shaped fallback
}
// Compact a magnitude for an axis tick: 80000 → "80k", 1_200_000 → "1.2M", 950 → "950".
// Lowercase "k" for thousands (exec-deck convention per request); uppercase M/B (lowercase
// would be ambiguous with milli/bit). Trims a whole number's trailing ".0".
function _compactNum(n) {
  const a = Math.abs(n);
  let div = 1, suf = "";
  if (a >= 1e9) { div = 1e9; suf = "B"; }
  else if (a >= 1e6) { div = 1e6; suf = "M"; }
  else if (a >= 1e3) { div = 1e3; suf = "k"; }
  const val = n / div;
  return val.toFixed(val % 1 === 0 ? 0 : 1) + suf;
}
// `decimalsOverride` (optional, roadmap: "the excel use three decimals, so ... follow the same
// precision as the reference table") — a chart series measure's OWN source-file numFmt decimal
// count (`blueprint_render`'s `chart_spec.decimals`, the chart-series counterpart to a Pinned
// Table cell's `decimals` — see `fmtValue`'s own comment for the full context). Only affects the
// `fmtValue` fallback branch below (percent/percent_scaled/ratio/plain-number formats) — a
// currency/number axis tick stays on its existing COMPACT notation (80k/1.2M) regardless, since
// a decimal-place override has no meaning for a magnitude-compacted tick label.
function fmtAxis(v, fmt, decimalsOverride) {
  const n = _num(v);
  if (n === null) return v;
  if (fmt === "currency") return (n < 0 ? "-" : "") + "$" + _compactNum(Math.abs(n));
  if (fmt === "number") return _compactNum(n);
  return fmtValue(v, fmt, decimalsOverride);
}
// Friendly X / category tick: turns time-bucket strings into readable period labels
// ("2025-01" → "Jan 2025", "2025-01-06" → "Jan 6, 2025", "2025-Q1" → "Q1 2025",
// "2025" → "2025"); any non-period category (e.g. "Studio", "West") passes through unchanged.
function fmtCategory(v) {
  const s = String(v == null ? "" : v).trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);           // full date
  if (m) { const mi = +m[2] - 1; if (mi >= 0 && mi < 12) return `${_MONTHS[mi]} ${+m[3]}, ${m[1]}`; }
  m = s.match(/^(\d{4})-(\d{2})$/);                       // year-month
  if (m) { const mi = +m[2] - 1; if (mi >= 0 && mi < 12) return `${_MONTHS[mi]} ${m[1]}`; }
  m = s.match(/^(\d{4})-?Q([1-4])$/i);                    // year-quarter
  if (m) return `Q${m[2]} ${m[1]}`;
  return s;
}

// A sortable numeric key for a date-LIKE category string, or null when the string doesn't match
// any recognized date shape (caller falls back to the original text-sort behavior unchanged).
//
// REAL BUG, found live-testing an actual customer file (Lakefront_Owner_Report.xlsx): a Blueprint
// line chart's dimension resolved to a column whose values are abbreviated month labels like
// "Sep 25"/"Aug 26" (month name + 2-digit year, NOT an ISO "YYYY-MM" period) — `orderedRows`'s
// `"chronological"` direction did a plain `localeCompare` on that text, which sorts ALPHABETICALLY
// ("Apr 26" < "Aug 26" < "Dec 25" < "Feb 26" ...), not by actual date order (Sep 25 -> ... -> Aug
// 26). The chart rendered with 6 of 12 months in a scrambled, non-chronological order despite the
// spec explicitly asking for `"chronological"` — the label was true in name only. Every OTHER
// category shape (a plain text label like "Studio"/"West", a numeric bucket) is UNAFFECTED: this
// helper returns null for anything that isn't recognizably date-shaped, and the caller's existing
// `localeCompare` fallback (identical to the pre-fix behavior) still applies in that case.
function _chronoSortKey(v) {
  const s = String(v == null ? "" : v).trim();
  if (!s) return null;
  // ISO date / year-month ("2025-01-06", "2025-01") — already sorts correctly with a plain
  // string compare, but computed here too so it shares ONE comparison path with the month-label
  // case below (never two divergent "chronological" implementations in the same function).
  let m = s.match(/^(\d{4})-(\d{2})(?:-(\d{2}))?$/);
  if (m) {
    const mi = +m[2] - 1;
    if (mi >= 0 && mi < 12) return (+m[1]) * 372 + mi * 31 + (m[3] ? +m[3] : 0);
  }
  // Year-quarter ("2025-Q1", "2025Q1").
  m = s.match(/^(\d{4})-?Q([1-4])$/i);
  if (m) return (+m[1]) * 372 + (+m[2] - 1) * 93;
  // Abbreviated (or full) month name + 2- or 4-digit year — "Sep 25", "Aug 26", "September 2026".
  // A bare 2-digit year is assumed 2000s/2100s split at 70 (matches the common "YY" convention;
  // this app's own reports only ever span recent/near-future dates, never pre-1970).
  m = s.match(/^([A-Za-z]{3,})\.?\s+(\d{2}|\d{4})$/);
  if (m) {
    const monIdx = _MONTHS.findIndex(mo => mo.toLowerCase() === m[1].slice(0, 3).toLowerCase());
    if (monIdx >= 0) {
      let yr = +m[2];
      if (yr < 100) yr += yr < 70 ? 2000 : 1900;
      return yr * 12 + monIdx;
    }
  }
  return null;   // not date-like — caller falls back to the original text sort
}

// Order rows per the deterministic chart_spec.order (ordinal / chronological / ranked).
function orderedRows(spec, rows) {
  const out = [...(rows || [])];
  const order = spec?.order || {};
  const x = spec?.x;
  if (order.direction === "ordinal" && Array.isArray(order.categories)) {
    const pos = {};
    order.categories.forEach((c, i) => { pos[String(c).toLowerCase()] = i; });
    out.sort((a, b) => (pos[String(a[x]).toLowerCase()] ?? 1e9) - (pos[String(b[x]).toLowerCase()] ?? 1e9));
  } else if (order.direction === "chronological") {
    out.sort((a, b) => {
      const ka = _chronoSortKey(a[x]), kb = _chronoSortKey(b[x]);
      // Both sides parsed as a real date-like value -> compare numerically (actual chronological
      // order). Either side didn't parse -> fall back to the ORIGINAL text sort, unchanged, so a
      // non-date "chronological" dimension (if one ever occurs) behaves exactly as it did before
      // this fix rather than silently changing behavior for a case this fix wasn't meant to touch.
      if (ka !== null && kb !== null) return ka - kb;
      return String(a[x]).localeCompare(String(b[x]));
    });
  }
  return out;
}

// ─── Small UI atoms ─────────────────────────────────────────────────────────────────────────
const card = {
  background: "#fff", border: "1px solid #e2e8f0", borderRadius: "14px", padding: "16px",
  boxShadow: "0 1px 2px rgba(16,24,40,.04)",
};

function Skeleton({ h = 200 }) {
  return <div className="animate-pulse" style={{ ...card, height: h, display: "flex", alignItems: "center", justifyContent: "center", color: "#cbd5e1", fontSize: 12 }}>Generating…</div>;
}

function DomainBadge({ domain, confidence, confirmedBy, options, open, onOpen, onSelect, onClose }) {
  const pretty = (k, l) => l || ((k || "base") === "base" ? "General" : (k[0].toUpperCase() + k.slice(1)));
  const current = (options || []).find(o => o.key === domain);
  const label = pretty(domain, current && current.label);
  const pending = confirmedBy === "pending";
  return (
    <span style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, color: "#334155" }}>
      <span title={`Confidence ${(Number(confidence) * 100 || 0).toFixed(0)}% · ${confirmedBy}`}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 999, background: pending ? "#fff7ed" : "#eef2ff", color: pending ? "#9a3412" : "#4f46e5", fontWeight: 700, border: `1px solid ${pending ? "#fed7aa" : "#e0e7ff"}` }}>
        Analyzing as: {label}{pending ? " (confirm?)" : ""}
      </span>
      <button onClick={onOpen} style={{ background: "none", border: "none", color: "#6366f1", cursor: "pointer", fontSize: 12, fontWeight: 600, textDecoration: "underline" }}>change</button>
      {open && (
        <>
          <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 6, zIndex: 50, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, boxShadow: "0 10px 28px rgba(15,23,42,.15)", minWidth: 210, padding: 6 }}>
            <div style={{ fontSize: 11, color: "#94a3b8", padding: "4px 8px", fontWeight: 600 }}>Analyze this project as</div>
            {options === null ? (
              <div style={{ fontSize: 12, color: "#94a3b8", padding: "6px 8px" }}>Loading…</div>
            ) : (options.length === 0 ? (
              <div style={{ fontSize: 12, color: "#94a3b8", padding: "6px 8px" }}>No options available</div>
            ) : options.map(o => (
              <button key={o.key} onClick={() => onSelect(o.key)}
                style={{ display: "block", width: "100%", textAlign: "left", padding: "7px 9px", border: "none", background: o.key === domain ? "#eef2ff" : "transparent", color: "#0f172a", borderRadius: 6, cursor: "pointer", fontSize: 12.5, fontWeight: o.key === domain ? 700 : 500 }}>
                {pretty(o.key, o.label)}{o.key === domain ? "  ✓" : ""}
              </button>
            )))}
          </div>
        </>
      )}
    </span>
  );
}

export { fmtValue };  // reused by tests/other views if needed

// ─── Chart renderer (driven purely by the deterministic chart_spec + annotations) ──────────
export function ChartView({ visual, palette, roles, maximized }) {
  const pal = (palette && palette.length) ? palette : PALETTE;
  const spec = visual.chart_spec || {};
  // Maximize view: every chart type below renders inside a fixed height:220 ResponsiveContainer
  // (the deck card's own compact size) — a maximized view gets real vertical room instead, the
  // whole point of the "see the complete one" request.
  const chartHeight = maximized ? 640 : 220;
  const orderedRaw = orderedRows(spec, visual.result_set || []);
  const series = spec.series || [];
  const x = spec.x;
  // Role colors (R23): a series with a resolved role (current/prior/target/threshold) uses the
  // theme's role color; everything else uses the ordinal palette. `roles` is populated only
  // when a brand theme is active, so flag-off render is unchanged.
  const seriesRoles = spec.series_roles || {};
  const colorFor = (name, i) => {
    const r = seriesRoles[name];
    return (r && roles && roles[r]) ? roles[r] : pal[i % pal.length];
  };
  // Athena returns every value as a STRING (e.g. "267900.0"); Recharts won't scale string
  // values, so bars/lines render flat at the baseline. Coerce the plotted series to real
  // numbers here (the x/category stays as-is). This is what makes the board charts actually
  // draw — without it a breakdown looks empty even though the data is present.
  const rows = orderedRaw.map((r) => {
    const o = { ...r };
    for (const s of series) o[s] = _num(o[s]);
    return o;
  });
  const fmts = spec.formats || {};
  const primaryFmt = fmts[series[0]] || "number";
  // Precision counterpart to `fmts` (roadmap: "the excel use three decimals, so ... follow the
  // same precision as the reference table") — `spec.decimals` (set by `blueprint_render`'s
  // `_measure_expr`/`_blueprint_visual_sql` from the Dictionary field's own source-file numFmt)
  // is optional and per-series-alias; `{}` when the backend found no such fact for any series in
  // this chart, which keeps every existing `fmtValue`/`fmtAxis` call's own format-key default
  // EXACTLY as before this existed (no override passed at all).
  const decimalsBySeries = spec.decimals || {};
  const primaryDecimals = decimalsBySeries[series[0]];
  const anns = visual.annotations || [];
  const refLines = anns.filter(a => a.kind === "reference_line");
  const anomalies = anns.filter(a => a.kind === "anomaly_callout");

  // GROUP-BY-COMPANY BANDS (roadmap #77, ported from AIInsightsPage.jsx's RenderChart — real
  // bug reported live: a chart pinned from Analyze with these bands lost them entirely on the
  // Dashboard, since this renderer never drew them at all). `spec.group_key` names a coarser
  // secondary category column already present on each row (e.g. an age-group axis breaking a
  // channel/campaign axis into blocks); the backend has already sorted rows into contiguous
  // per-group blocks (`ai_insights.py`'s `group_key` detection), so drawing one labeled band per
  // contiguous run is enough — no re-sorting needed here. Degrades to nothing when absent/<2
  // groups, identical to the Analyze renderer's own guard.
  const groupKey = (spec.group_key && rows[0] && rows[0][spec.group_key] !== undefined) ? spec.group_key : null;
  const bandGroups = [];
  if (groupKey) {
    rows.forEach((row, i) => {
      const g = String(row[groupKey] ?? "");
      const last = bandGroups[bandGroups.length - 1];
      if (last && last.name === g) last.end = i;
      else bandGroups.push({ name: g, start: i, end: i });
    });
  }
  const hasBands = !!groupKey && bandGroups.length >= 2;
  const CompanyBands = (props) => {
    const off = props.offset || {};
    const { left, top, width, height } = off;
    const n = rows.length;
    if (!n || !width) return null;
    const band = width / n;
    const els = [];
    bandGroups.forEach((g, gi) => {
      const x0 = left + g.start * band;
      const x1 = left + (g.end + 1) * band;
      const cx = (x0 + x1) / 2;
      els.push(<text key={`bl-${gi}`} x={cx} y={top - 20} textAnchor="middle" fontSize={12} fontWeight={700} fill="#334155">{g.name || "Other"}</text>);
      els.push(<line key={`bu-${gi}`} x1={x0 + 5} y1={top - 12} x2={x1 - 5} y2={top - 12} stroke="#94a3b8" strokeWidth={2} />);
      if (gi > 0) els.push(<line key={`bd-${gi}`} x1={x0} y1={top - 6} x2={x0} y2={top + height} stroke="#cbd5e1" strokeWidth={1} strokeDasharray="4 4" />);
    });
    return <g>{els}</g>;
  };
  const bandTopMargin = hasBands ? 46 : 16;

  const tip = (v) => fmtValue(v, primaryFmt, primaryDecimals);
  const axis = (v) => fmtAxis(v, primaryFmt, primaryDecimals);
  const grid = <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />;
  const common = { data: rows, margin: { top: bandTopMargin, right: 16, bottom: 4, left: 4 } };

  if (spec.type === "line") {
    return (
      <ResponsiveContainer width="100%" height={chartHeight}>
        <LineChart {...common}>
          {grid}
          <XAxis dataKey={x} tick={{ fontSize: 11 }} tickFormatter={fmtCategory} />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={axis} />
          <Tooltip formatter={tip} labelFormatter={fmtCategory} />
          {series.length > 1 && <Legend />}
          {refLines.map((r, i) => <ReferenceLine key={i} y={r.value} stroke={r.color} strokeDasharray="4 4" label={{ value: r.label, position: "right", fontSize: 10, fill: r.color }} />)}
          {series.map((s, i) => <Line key={s} type="monotone" dataKey={s} stroke={colorFor(s, i)} strokeWidth={2} dot={false}
            label={series.length === 1 && rows.length <= 24 ? { position: "top", fontSize: 9, fill: "#64748b", formatter: (val) => fmtAxis(val, primaryFmt, primaryDecimals) } : undefined} />)}
          {anomalies.map((a, i) => <ReferenceLine key={`a${i}`} x={a.x} stroke={a.color} strokeDasharray="2 2" label={{ value: a.label, position: "top", fontSize: 9, fill: a.color }} />)}
        </LineChart>
      </ResponsiveContainer>
    );
  }
  // Advanced primitives (R26): dual-axis combo (bars + secondary-axis line, #8) and stacked
  // columns (#4). Driven purely by the deterministic spec — series_types (bar|line), axis
  // (primary|secondary), stack (group id). No math here; the engine decided the shape.
  if (spec.type === "combo" || spec.type === "stacked_bar") {
    const stypes = spec.series_types || {};
    const axes = spec.axis || {};
    const stacks = spec.stack || {};
    const primarySeries = series.filter((s) => axes[s] !== "secondary");
    const secondarySeries = series.filter((s) => axes[s] === "secondary");
    const hasSecondary = secondarySeries.length > 0;
    const leftFmt = fmts[primarySeries[0]] || primaryFmt;
    const rightFmt = fmts[secondarySeries[0]] || "percent";
    const fmtFor = (name) => fmts[name] || primaryFmt;
    const decimalsFor = (name) => decimalsBySeries[name];
    return (
      <ResponsiveContainer width="100%" height={chartHeight}>
        <ComposedChart {...common}>
          {grid}
          {hasBands && <Customized component={CompanyBands} />}
          <XAxis dataKey={x} tick={{ fontSize: 11 }} tickFormatter={fmtCategory} />
          <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={(v) => fmtAxis(v, leftFmt, decimalsBySeries[primarySeries[0]])} />
          {hasSecondary && <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => fmtAxis(v, rightFmt, decimalsBySeries[secondarySeries[0]])} />}
          <Tooltip formatter={(val, name) => fmtValue(val, fmtFor(name), decimalsFor(name))} labelFormatter={fmtCategory} />
          <Legend />
          {refLines.map((r, i) => <ReferenceLine key={i} yAxisId="left" y={r.value} stroke={r.color} strokeDasharray="4 4" label={{ value: r.label, position: "right", fontSize: 10, fill: r.color }} />)}
          {series.map((s, i) => {
            const yId = axes[s] === "secondary" ? "right" : "left";
            if (stypes[s] === "line") {
              return <Line key={s} yAxisId={yId} type="monotone" dataKey={s} stroke={colorFor(s, i)} strokeWidth={2} dot={false}
                label={rows.length <= 24 ? { position: "top", fontSize: 9, fill: "#64748b", formatter: (val) => fmtAxis(val, fmts[s] || primaryFmt, decimalsFor(s)) } : undefined} />;
            }
            return <Bar key={s} yAxisId={yId} dataKey={s} fill={colorFor(s, i)} stackId={stacks[s] || undefined} radius={[4, 4, 0, 0]}
              label={rows.length <= 24 ? { position: stacks[s] ? "center" : "top", fontSize: 9, fill: stacks[s] ? "#fff" : "#475569", formatter: (val) => fmtAxis(val, fmts[s] || primaryFmt, decimalsFor(s)) } : undefined} />;
          })}
        </ComposedChart>
      </ResponsiveContainer>
    );
  }
  if (spec.type === "donut") {
    const m = series[0];
    return (
      <ResponsiveContainer width="100%" height={chartHeight}>
        <PieChart>
          <Pie data={rows} dataKey={m} nameKey={x} innerRadius={45} outerRadius={80} paddingAngle={2}>
            {rows.map((r, i) => <Cell key={i} fill={pal[i % pal.length]} />)}
          </Pie>
          <Tooltip formatter={tip} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    );
  }
  // hbar (ranked, horizontal), grouped_bar, and default bar
  const horizontal = spec.type === "hbar";
  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart {...common} layout={horizontal ? "vertical" : "horizontal"}>
        {grid}
        {hasBands && !horizontal && <Customized component={CompanyBands} />}
        {horizontal
          ? (<><XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={axis} /><YAxis type="category" dataKey={x} width={110} tick={{ fontSize: 11 }} tickFormatter={fmtCategory} /></>)
          : (<><XAxis dataKey={x} tick={{ fontSize: 11 }} tickFormatter={fmtCategory} /><YAxis tick={{ fontSize: 11 }} tickFormatter={axis} /></>)}
        <Tooltip formatter={tip} labelFormatter={fmtCategory} />
        {series.length > 1 && <Legend />}
        {refLines.map((r, i) => <ReferenceLine key={i} {...(horizontal ? { x: r.value } : { y: r.value })} stroke={r.color} strokeDasharray="4 4" label={{ value: r.label, position: horizontal ? "top" : "right", fontSize: 10, fill: r.color }} />)}
        {series.map((s, i) => <Bar key={s} dataKey={s} fill={colorFor(s, i)} radius={horizontal ? [0, 6, 6, 0] : [6, 6, 0, 0]}
          label={series.length === 1 && rows.length <= 24 ? { position: horizontal ? "right" : "top", fontSize: 9, fill: "#475569", formatter: (val) => fmtAxis(val, fmts[s] || primaryFmt, decimalsBySeries[s]) } : undefined} />)}
      </BarChart>
    </ResponsiveContainer>
  );
}

// ─── Pinned Table renderer (blueprint-tables-and-concepts R7) — `grid_table`/`stat_card_table`
// consume `blueprint_render.grid_table_chart_spec`/`stat_card_table_chart_spec`'s exact output
// shape verbatim: `{"type": "grid_table", "columns": [...], "rows": [{"row", "cells": {col:
// {"value","kind","reason"}}}]}` or `{"type": "stat_card_table", "items": [{"label","value",
// "kind","reason","format"}]}`. Every cell keeps its OWN kind/reason (never flattened to a bare
// value, per that function's own docstring) — an unresolved/needs-data cell renders a small
// inline placeholder instead of a fabricated number, mirroring the chart-level "Needs data" pill
// one level deeper into a table's own cells (R4.2's "one bad visual/cell never blocks the rest",
// applied here to ONE cell inside an otherwise-fine table). ──
function _tableCellDisplay(cell) {
  // fix-source-routing (F3): the reason is also visible below the table (TableView's
  // "couldn't be calculated" notes) — the hover title here is no longer the only place it lives.
  if (cell.kind === "unresolved" || cell.kind === "needs_data") {
    return <span title={cell.reason || "Data not available for this view"} style={{ color: "#b45309", fontSize: 11, fontStyle: "italic" }}>—</span>;
  }
  if (cell.value === null || cell.value === undefined) {
    return <span style={{ color: "#cbd5e1" }}>—</span>;
  }
  // Presentable table values (roadmap: "consider formatting the table values, numeric, emails,
  // phone numbers, dates, etc."): `cell.format` (blueprint_render.py's `_cell_format_key`) names
  // the workbook's OWN style-derived format — currency/percent/date/number for a "measure"/
  // "constant" cell, or a light content-based "email"/"phone" guess. `email`/`phone` render as
  // real clickable links (a customer-facing table with a contact column is a real Pinned-Table
  // use case — an unclickable, unstyled email/number string isn't "presentable"); every numeric
  // format key reuses `fmtValue` exactly like a chart cell already does, so a table and a chart
  // never disagree on how the SAME format key displays.
  if (cell.format === "email" && typeof cell.value === "string") {
    return <a href={`mailto:${cell.value}`} style={{ color: "#4f46e5", textDecoration: "none" }}>{cell.value}</a>;
  }
  if (cell.format === "phone" && typeof cell.value === "string") {
    return <a href={`tel:${cell.value.replace(/[^\d+]/g, "")}`} style={{ color: "#4f46e5", textDecoration: "none" }}>{cell.value}</a>;
  }
  if (cell.format === "date") return fmtValue(cell.value, "date");
  // REAL BUG, found live-testing the actual KIWI file: a "constant" table cell (R6.1 — the
  // backend copies its value VERBATIM from Excel's own cached formula/typed result, by design,
  // never parsed as a number) always arrives here as a STRING over JSON, even when it's plainly
  // numeric — e.g. Excel's own cached result for a tiny ratio-formula cell literally reads
  // "4.4999999999999998E-2" (scientific notation) or "4232804.2328042332" (an unrounded long
  // decimal). The old `typeof cell.value === "number"` check only ever matched a "measure"/
  // "concept" cell (those DO arrive as real JSON numbers, straight from Athena), so a numeric-
  // looking constant fell through to `String(cell.value)` and printed Excel's raw cached text
  // completely unformatted — even though `cell.format` correctly said "number"/"currency" the
  // whole time. Fix: attempt numeric coercion on ANY value (not just ones already typed as a JS
  // number) via `_num` (already handles scientific notation through `parseFloat`, already
  // strips $/,/% for a pre-formatted string) — a genuine text label (e.g. "Meta", "Kiwi") fails
  // that parse and safely falls through to the plain string render below, so this can never
  // mis-render a real word as a number.
  // Precision (roadmap: "the excel use three decimals, so the tables has to follow the same
  // precision as the reference table"): `cell.decimals` (blueprint_render.py's
  // `_cell_format_decimals`) is this cell's OWN source-file numFmt decimal count when known —
  // undefined for a cell with no resolvable style, which keeps `fmtValue`'s own format-key
  // default (unchanged pre-existing behavior for those cells).
  if (_num(cell.value) !== null) return fmtValue(cell.value, cell.format || "number", cell.decimals);
  return String(cell.value);
}

function GridTableView({ spec, maximized }) {
  const columns = spec.columns || [];
  const rows = spec.rows || [];
  // Real bug reported live: "the table (grid) is not well formatted, and nothing close to how
  // it looks like in the Excel." Root cause traced against the real backend chart_spec (its
  // columns/rows/values ARE correct — verified against live Dev data) — this component itself
  // rendered NO header row at all (every <tr> was a plain data row, `columns` was used only to
  // look up each row's cells, never displayed), so a spreadsheet range with its own header row
  // read as an undifferentiated wall of numbers with nothing to orient by. Fixes, all purely
  // presentational (the underlying cell data/values are untouched):
  // 1. The table's OWN first row (top row of the confirmed range, e.g. a pivot's own column
  //    labels) renders as a distinct, bolded header — the standard spreadsheet convention —
  //    using the app's own PRIMARY brand color, the same blue every chart's own primary series
  //    already uses, instead of blending into the data rows below.
  // 2. Real borders on every cell (not just a bottom rule), alternating row shading, and
  //    right-aligned numeric values — closer to how a grid actually reads in Excel, without
  //    attempting pixel-exact style replication (this component has no access to the source
  //    file's actual cell formatting/merges, only the resolved value+kind per cell).
  //
  // Roadmap follow-up ("the table is too simple, make it more visually appealing... the same
  // theme has to be respected in the export"): pulls its look from `designTokens.TABLE_STYLE`
  // (mirrors `design_system.TABLE_STYLE`, the SAME tokens `export_xlsx.py`/`export_pptx.py` now
  // use for a Pinned Table sheet/slide) instead of ad-hoc hex literals, so a brand-primary color
  // change or a future table-theme tweak updates in-app AND export from ONE place. Adds a sticky
  // header (stays visible while scrolling a tall table, esp. in the Maximize view) and a
  // distinct, bolded Total/Grand-Total row style (`cell.is_total_row`, computed once on the
  // backend by `blueprint_render.grid_table_chart_spec` from the row's OWN label text — e.g.
  // Excel's own "Total general" — never guessed from the row's values).
  const [headerRow, ...bodyRows] = rows;
  const cellBorder = `1px solid ${TABLE_STYLE.border}`;
  const headerColor = TABLE_STYLE.header_bg;
  // Maximize view: a bit more breathing room (font/padding) now that the table isn't fighting
  // the deck card's compact width — same data/values, just easier to actually read.
  const fontSize = maximized ? 13.5 : 12;
  const cellPad = maximized ? "9px 14px" : "6px 10px";
  const isTotalRow = (r) => columns.some((col) => (r.cells || {})[col] && (r.cells || {})[col].is_total_row);
  return (
    <div style={{ overflowX: "auto", overflowY: maximized ? "auto" : undefined, maxHeight: maximized ? "70vh" : undefined, border: `1px solid ${headerColor}33`, borderRadius: 10, boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize }}>
        {headerRow && (
          <thead>
            <tr>
              {columns.map((col) => {
                const cell = (headerRow.cells || {})[col];
                return (
                  <th key={col} style={{ position: "sticky", top: 0, zIndex: 1, padding: cellPad, border: cellBorder, borderBottom: `2px solid ${headerColor}`, background: headerColor, color: TABLE_STYLE.header_text, fontWeight: 700, textAlign: "left", whiteSpace: "nowrap" }}>
                    {cell ? _tableCellDisplay(cell) : ""}
                  </th>
                );
              })}
            </tr>
          </thead>
        )}
        <tbody>
          {bodyRows.map((r, i) => {
            const total = isTotalRow(r);
            return (
              <tr key={r.row} style={total
                ? { background: TABLE_STYLE.total_bg, borderTop: `2px solid ${TABLE_STYLE.total_border}` }
                : { background: i % 2 === 1 ? TABLE_STYLE.band_bg : TABLE_STYLE.row_bg }}>
                {columns.map((col) => {
                  const cell = (r.cells || {})[col];
                  // REAL BUG (same root cause as `_tableCellDisplay`'s own fix): a numeric
                  // "constant" cell always arrives as a STRING, so the old `typeof cell.value ===
                  // "number"` check wrongly left-aligned it like a text cell (a number's
                  // conventional right-alignment for a spreadsheet-style table never applied to
                  // it). Reuse the SAME `_num` coercion the display function uses, so a cell's
                  // alignment and its rendered VALUE always agree on whether it's numeric.
                  const isNumeric = cell && cell.format !== "email" && cell.format !== "phone" && _num(cell.value) !== null;
                  return (
                    <td key={col} style={{ padding: cellPad, border: cellBorder, color: total ? TABLE_STYLE.total_text : TABLE_STYLE.text, fontWeight: total ? 700 : 400, whiteSpace: "nowrap", textAlign: isNumeric ? "right" : "left" }}>
                      {cell ? _tableCellDisplay(cell) : ""}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StatCardTableView({ spec }) {
  const items = spec.items || [];
  const accent = PALETTE[0];
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
      {items.map((it, i) => (
        <div key={i} style={{ flex: "1 1 100px", minWidth: 90, border: "1px solid #e2e8f0", borderLeft: `3px solid ${accent}`, borderRadius: 10, padding: "10px 12px", background: "#f8fafc" }}>
          {it.label && <div style={{ fontSize: 10.5, color: "#64748b", marginBottom: 4 }}>{it.label}</div>}
          <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>
            {it.kind === "unresolved" || it.kind === "needs_data"
              ? <span title={it.reason || ""} style={{ fontSize: 12, color: "#b45309", fontWeight: 600 }}>Needs data</span>
              : _tableCellDisplay(it)}
          </div>
        </div>
      ))}
    </div>
  );
}

// One entry point for either Pinned Table shape — same role `ChartView` plays for a chart,
// dispatched from `VisualCard` alongside its existing `isStat`/`ChartView` branches.
export function TableView({ visual, maximized }) {
  const spec = visual.chart_spec || {};
  const view = spec.type === "stat_card_table"
    ? <StatCardTableView spec={spec} />
    : <GridTableView spec={spec} maximized={maximized} />;
  // #184: non-blocking data notes (e.g. Excel's "(blank)" pivot row shown empty) are SAID on
  // the dashboard too, never hidden (`blueprint_render.render_pinned_table`'s `notes`).
  const notes = Array.isArray(visual.notes) ? visual.notes : [];
  // #188: the Excel pivot filters this table applies (read-only chips, above the table) and how
  // its LIVE values relate to the Excel it was imported from (`reconcile_note`). Values shown are
  // always calculated from the uploaded data — never Excel's saved numbers.
  const hasChips = chipsForTable(visual).length > 0;
  const recNote = reconcileNoteForVisual(visual);
  // fix-source-routing (F3): every group of "—" cells says WHY, visibly (not hover-only).
  const uncalcNotes = uncalculableNotesForVisual(visual);
  if (!notes.length && !hasChips && !recNote && !uncalcNotes.length) return view;
  return (
    <>
      {hasChips && <TableFilterChips table={visual} style={{ margin: "0 0 8px" }} />}
      {view}
      {notes.map((n) => (
        <div key={`${n.code}-${n.row}`} role="note" style={{ fontSize: 11, color: "#475569", marginTop: 6, lineHeight: 1.4 }}>
          ⓘ {n.text}
        </div>
      ))}
      {uncalcNotes.map((n) => (
        <div key={n.key} role="note" style={{ fontSize: 11, color: "#92400e", marginTop: 6, lineHeight: 1.4 }}>
          ⚠ {n.text}
        </div>
      ))}
      {recNote && (
        <div role="note" style={recNote.severity === "warning"
          ? { fontSize: 11, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "5px 8px", marginTop: 6, lineHeight: 1.4 }
          : { fontSize: 11, color: "#475569", marginTop: 6, lineHeight: 1.4 }}>
          {recNote.severity === "warning" ? "⚠" : "ⓘ"} {recNote.text}
        </div>
      )}
    </>
  );
}

// Map a machine failure reason to a short, plain-language explanation for the "Needs data" state.
function friendlyFailReason(reason) {
  const r = String(reason || "").toLowerCase();
  if (r.includes("empty") || r.includes("no_data") || r.includes("no rows")) return "No matching rows in this project's data for this metric yet.";
  if (r.includes("insufficient") || r.includes("not enough") || r.includes("enough data")) return "This project's data doesn't have the columns needed to compute this metric (e.g. cost and conversions).";
  if (r.includes("numeric") || r.includes("cast") || r.includes("type")) return "A column this metric relies on isn't reading as a number in the source data.";
  if (r.includes("column") || r.includes("missing")) return "A column this metric needs isn't present in this project's data.";
  return reason ? `Couldn't build this visual (${reason}).` : "This metric can't be computed from the current data.";
}
// (fixed-visual-blueprint R14.1/R14.4/Task 28, extended roadmap #95) A Blueprint needs-data
// card's `reasonCode` distinguishes MISSING COLUMN / MISSING ROWS / a DIMENSION-MEASURE
// COLLISION — each gets its own next-step hint so the plain-language reason isn't the
// customer's only clue (mirrors backend/app/insights/blueprint_render.py's _NEEDS_REASONS).
function needsDataHint(reasonCode) {
  if (reasonCode === "unmapped_column") {
    return "Upload a file with this column, or reactivate an archived file that has it, from the Data screen.";
  }
  if (reasonCode === "no_rows") {
    return "No rows matched — check that the right file is active for this project on the Data screen.";
  }
  if (reasonCode === "dimension_conflicts_measure") {
    // Deliberately NOT "upload a file" — no upload fixes a breakdown field that was never in
    // the original report's data model. This is a template-mapping limitation, not a data gap
    // an upload can close, so the hint says so plainly instead of sending the customer on a
    // pointless re-upload.
    return "Your reference report's chart needs this breakdown as its own column, but none of your uploaded data has it. This chart will stay hidden here until that column exists in your data.";
  }
  return null;
}

// ─── Two labeled sections (fixed-visual-blueprint R21.4-R21.7, Task 26) ─────────────────────
// Section 1: the AI-selected ("Smart AI") or Blueprint-selected ("From Template") cards — the
// ONLY section a template publish/switch ever replaces. Section 2: "My Additions" — the user's
// pinned/custom cards, sourced from the SAME `_isPinnedCard`/board data every mode already uses,
// rendered identically regardless of boardMode (R21.4's "reusing the existing custom-visual
// store so pinned cards render identically in both modes").
function SectionLabel({ label, icon, color, count, tableCount }) {
  // Real bug reported live: "the analysis indicates there are 18 charts but after publishing
  // those, it indicates 19." Root cause — a Pinned Table renders as an entry in this SAME
  // section's list (blueprint_render._build puts charts + tables in one board.visuals array by
  // design), but the wizard's "Extracted 18 charts" count is chart-only. `count` here used to be
  // the RAW list length (18 charts + 1 table = 19), which read as an unexplained mismatch/
  // duplicate rather than what it actually is. Split the badge so the chart count always matches
  // what the wizard promised, with the table called out separately alongside it.
  const chartCount = tableCount ? count - tableCount : count;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "6px 0 2px" }}>
      <span style={{ fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", color, display: "flex", alignItems: "center", gap: 6 }}>
        {icon} {label}
        <span style={{ background: "#f1f5f9", color: "#64748b", borderRadius: 999, padding: "1px 8px", fontSize: 11, fontWeight: 600 }}>
          {tableCount ? `${chartCount} chart${chartCount === 1 ? "" : "s"} · ${tableCount} table${tableCount === 1 ? "" : "s"}` : count}
        </span>
      </span>
      <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
    </div>
  );
}

function TwoSectionGrid({ shown, isPinnedCard, boardMode, chromeless, onAudit, onRegenerate, onDrill, onPin, onRemove, pinnedSet, palette, roles }) {
  const primary = shown.filter(v => !isPinnedCard(v));
  const mine = shown.filter(isPinnedCard);
  const isBlueprint = boardMode === "blueprint";
  const gridClass = `autodeck-grid${chromeless ? " autodeck-grid-wide" : ""}`;

  const renderCard = (v, fromTemplate) => (
    <VisualCard key={v.id} visual={v} onAudit={onAudit} onRegenerate={onRegenerate}
      onDrill={onDrill} onPin={onPin} onRemove={onRemove} isPinned={pinnedSet.has(v.id)}
      palette={palette} roles={roles} fromTemplate={fromTemplate} />
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {primary.length > 0 && (
        <>
          <SectionLabel
            label={isBlueprint ? "From Template" : "Smart AI"}
            icon={isBlueprint ? "📊" : "🤖"}
            color={isBlueprint ? "#7c3aed" : "#2563eb"}
            count={primary.length}
            tableCount={primary.filter(v => v.kind === "grid_table" || v.kind === "stat_card_table").length}
          />
          {/* Template-origin left-border accent (roadmap #95): the section header already says
              "From Template", but a card can end up viewed out of that context (a drill-down
              modal, a screenshot, a PPTX slide) where the header isn't visible — a subtle
              per-card marker, reusing the SAME purple as the section label/Template-active pill,
              makes the origin unambiguous wherever the card is seen. Only applies to primary
              (non-pinned) cards; "My Additions" is never template-origin (R21.4/21.5). */}
          <div className={gridClass}>{primary.map(v => renderCard(v, isBlueprint))}</div>
        </>
      )}
      {/* "My Additions" always renders (even if empty is fine to skip) — never gated by
          boardMode, so a template publish/switch (R21.5/R21.6) never touches it. */}
      {mine.length > 0 && (
        <>
          <SectionLabel label="My Additions" icon="📌" color="#b45309" count={mine.length} />
          <div className={gridClass}>{mine.map(v => renderCard(v, false))}</div>
        </>
      )}
    </div>
  );
}

// ─── One visual (stat card or chart), with audit + regenerate + drill-down ──────────────────
function VisualCard({ visual, onAudit, onRegenerate, onDrill, onPin, onRemove, isPinned, palette, roles, fromTemplate }) {
  // A Blueprint visual whose data is absent renders the SAME graceful "Needs data" state as a
  // failed one (fixed-visual-blueprint R4.1): the slot stays in place with a plain-language
  // reason — never a blank card and never a fabricated value.
  const needsData = visual.status === "needs_data";
  const failed = visual.status === "failed" || needsData;
  const pending = visual.status === "pending";
  if (pending) return <div style={{ gridColumn: "span 2" }}><Skeleton /></div>;

  const isStat = (visual.chart_spec || {}).type === "stat";
  const isTable = visual.kind === "grid_table" || visual.kind === "stat_card_table";
  // Maximize view (roadmap: "option to maximize the view of the graphs and table so we can see
  // the complete one") — a chart/table crammed into the deck's fixed card height/width is the
  // exact complaint; a stat card has no such problem (it's already just a number + a pill), so
  // the button is scoped to chart/table only, same scoping `onRegenerate` already uses above.
  const [maximized, setMaximized] = useState(false);
  // KPI semantic tint (R24): fill/border/text/dot from the deterministic tint state (neutral
  // when the KPI has no target to compare against).
  const tint = isStat ? (KPI_TINT[visual.tintState] || KPI_TINT.neutral) : null;
  // vs-target pill colors (real: only shown when the project has a goal for this KPI → tintState good/bad).
  const pillStyle = {
    good: { color: "#059669", background: "#ecfdf5", border: "1px solid #a7f3d0" },
    bad: { color: "#e11d48", background: "#fef2f4", border: "1px solid #fecdd3" },
    neutral: { color: "#64748b", background: "#f1f5f9", border: "1px solid #e2e8f0" },
  }[visual.tintState] || { color: "#64748b", background: "#f1f5f9", border: "1px solid #e2e8f0" };
  const miniBtn = { fontSize: 11, fontWeight: 700, padding: "5px 10px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", cursor: "pointer" };
  return (
    <div id={`visual-${visual.id}`} className="deck-card" title={fromTemplate ? "From your uploaded template" : undefined} style={{ ...card, gridColumn: (isStat && !isTable) ? "span 1" : "span 2", display: "flex", flexDirection: "column", gap: 8,
      background: tint ? tint.fill : "#fff",
      borderColor: isPinned ? "#c7d2fe" : (tint ? tint.border : "#e2e8f0"),
      borderLeft: fromTemplate ? "3px solid #7c3aed" : undefined }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", display: "inline-flex", alignItems: "center" }}>
          {tint && <span style={{ width: 8, height: 8, borderRadius: 999, background: tint.dot, display: "inline-block", marginRight: 6, flexShrink: 0 }} />}
          {isPinned ? "📌 " : ""}{visual.title}
          {visual.kind === "custom" && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#4f46e5", background: "#eef2ff", border: "1px solid #e0e7ff", borderRadius: 999, padding: "1px 7px", verticalAlign: "middle" }}>Custom</span>}
        </span>
        {/* Card tools reveal on hover/focus (executive-clean default; roadmap #77 redesign). */}
        <div className="deck-card-tools" style={{ display: "flex", gap: 6, flex: "0 0 auto" }}>
          <IconBtn title={isPinned ? "Unpin" : "Pin to top"} onClick={() => onPin(visual.id)}>{isPinned ? "📌" : "📍"}</IconBtn>
          <IconBtn title="How this was computed" onClick={() => onAudit(visual)}>ⓘ</IconBtn>
          {/* Regenerate is a chart-only action today — board.regenerate_visual only recomputes
              a visual with a stored `_plan` (never a Pinned Table, which has none); clicking it
              on a table would silently no-op, which reads as broken rather than as "nothing to
              regenerate," so the button is hidden for a table kind entirely. */}
          {visual.kind !== "custom" && !isTable && <IconBtn title="Regenerate this visual" onClick={() => onRegenerate(visual.id)}>⟳</IconBtn>}
          {!failed && (isTable || !isStat) && (
            <IconBtn title="Maximize" onClick={() => setMaximized(true)}><Maximize2 size={13} /></IconBtn>
          )}
          <IconBtn title={visual.kind === "custom" ? "Remove this custom view" : "Remove from board"} onClick={() => onRemove(visual.id)}>✕</IconBtn>
        </div>
      </div>
      {/* fix-source-routing: which ONE data source this table/chart reads (backend wording), and
          a pinned table's source warning (source gone / couldn't be confirmed) — always visible. */}
      {sourceLabelFor(visual) && (
        <div style={{ fontSize: 11, color: "#64748b", marginTop: -4 }}>{sourceLabelFor(visual)}</div>
      )}
      {visual.source_note && visual.source_note.text && (
        <div role="note" style={{ fontSize: 11, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "5px 8px", lineHeight: 1.4 }}>
          ⚠ {visual.source_note.text}
        </div>
      )}
      {failed ? (
        // Graceful "Needs data" state (roadmap #77): a compact pill + plain-language reason + REAL
        // one-click actions (view the computation logic / ask in Analyze). No fake "re-map field".
        <div style={{ display: "flex", flexDirection: "column", gap: 9, padding: "10px 0 4px" }}>
          <span style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 800, color: "#b45309", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 999, padding: "3px 10px" }}>⚠︎ Needs data</span>
          <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.5 }}>{friendlyFailReason(visual.reason)}</div>
          {/* Task 28 — a missing-column/missing-data hint, distinct from the generic reason,
              pointing to the actual next step (upload/reactivate on the Data screen). */}
          {needsDataHint(visual.reasonCode) && (
            <div style={{ fontSize: 11.5, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "6px 9px", lineHeight: 1.4 }}>
              💡 {needsDataHint(visual.reasonCode)}
            </div>
          )}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button style={miniBtn} onClick={() => onAudit(visual)}>View data logic</button>
            {onDrill && <button style={miniBtn} onClick={() => onDrill(`What data is needed to compute "${visual.title}"?`)}>Ask in Analyze</button>}
          </div>
        </div>
      ) : isStat ? (
        <div style={{ padding: "6px 0", position: "relative" }}>
          {/* Faint per-month trend sparkline behind the number (roadmap #77 #1). Color follows
              the delta's good/bad direction (green up-good / red bad), else neutral indigo. */}
          {Array.isArray(visual.trend) && visual.trend.length >= 2 && (
            <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 30, opacity: 0.3, pointerEvents: "none" }}>
              <Sparkline values={visual.trend} color={visual.deltaGood === true ? "#4ade80" : visual.deltaGood === false ? "#f87171" : "#a5b4fc"} />
            </div>
          )}
          <div style={{ position: "relative" }}>
            <div style={{ fontSize: 34, fontWeight: 800, color: tint ? tint.text : "#0f172a", lineHeight: 1.1 }}>{visual.displayValue}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
              {/* Prior-period delta pill — real (computed from the KPI's own monthly trend). */}
              {typeof visual.deltaPct === "number" && (
                <span style={{ fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 999,
                  color: visual.deltaGood === true ? "#059669" : visual.deltaGood === false ? "#e11d48" : "#64748b",
                  background: visual.deltaGood === true ? "#ecfdf5" : visual.deltaGood === false ? "#fef2f4" : "#f1f5f9",
                  border: `1px solid ${visual.deltaGood === true ? "#a7f3d0" : visual.deltaGood === false ? "#fecdd3" : "#e2e8f0"}` }}>
                  {visual.deltaPct >= 0 ? "▲ " : "▼ "}{Math.abs(visual.deltaPct * 100).toFixed(1)}% vs prior mo.
                </span>
              )}
              {visual.tintReference && (
                <span style={{ fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 999, ...pillStyle }}>
                  {visual.tintState === "good" ? "▲ " : visual.tintState === "bad" ? "▼ " : ""}vs {visual.tintReference} target
                </span>
              )}
              {visual.formula_text && <span style={{ fontSize: 11, color: "#94a3b8" }}>{visual.formula_text}</span>}
            </div>
          </div>
        </div>
      ) : isTable ? (
        <TableView visual={visual} />
      ) : (
        <ChartView visual={visual} palette={palette} roles={roles} />
      )}
      {!failed && visual.narrative && (
        <div style={{ fontSize: 12, color: "#475569", lineHeight: 1.5 }}>{visual.narrative}</div>
      )}
      {!failed && !isStat && !isTable && onDrill && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 2 }}>
          {[`Break "${visual.title}" down further`, `Show "${visual.title}" as a table`, `What changed in "${visual.title}" over time?`].map((q, i) => (
            <button key={i} onClick={() => onDrill(q)} style={{ fontSize: 11, padding: "4px 9px", borderRadius: 999, border: "1px solid #e0e7ff", background: "#f5f7ff", color: "#4f46e5", cursor: "pointer" }}>{["Break down", "As table", "Over time"][i]}</button>
          ))}
        </div>
      )}
      {maximized && (
        <MaximizeModal title={visual.title} onClose={() => setMaximized(false)}>
          {isTable ? <TableView visual={visual} maximized /> : <ChartView visual={visual} palette={palette} roles={roles} maximized />}
        </MaximizeModal>
      )}
    </div>
  );
}

function IconBtn({ children, onClick, title }) {
  return <button title={title} onClick={onClick} style={{ width: 26, height: 26, borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", cursor: "pointer", fontSize: 13, lineHeight: 1 }}>{children}</button>;
}

// ─── Maximize view (roadmap: "option to maximize the view of the graphs and table so we can see
// the complete one") — a large, near-fullscreen overlay reusing this app's existing modal
// conventions (MainLayout.jsx's changelog modal: fixed inset:0 overlay, blurred backdrop,
// centered white card, sticky header with a close button) rather than the browser's native
// Fullscreen API — more reliable across browsers (no permission prompts, no vendor-prefixed
// API differences) and visually consistent with every other dialog already in this app. Closes
// on Escape or a backdrop click, mirroring the changelog modal's own backdrop-click-to-close. ──
function MaximizeModal({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 10002, background: "rgba(2,6,23,.6)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
      backdropFilter: "blur(3px)",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 16, width: "min(1400px, 96vw)", height: "min(900px, 92vh)",
        display: "flex", flexDirection: "column", boxShadow: "0 24px 60px rgba(2,6,23,.4)", overflow: "hidden",
      }}>
        <div style={{
          padding: "16px 20px", borderBottom: "1px solid #e2e8f0", display: "flex",
          justifyContent: "space-between", alignItems: "center", flex: "0 0 auto",
        }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>{title}</span>
          <button title="Close" onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex" }}>
            <X size={20} />
          </button>
        </div>
        <div style={{ flex: "1 1 auto", overflow: "auto", padding: 20 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

// Tiny dependency-free sparkline (roadmap #77 #1): the KPI's per-month trend, drawn as a scaled
// polyline. Rendered faintly behind the KPI number. Needs >= 2 points; scales to its container.
function Sparkline({ values, color = "#a5b4fc" }) {
  if (!values || values.length < 2) return null;
  const W = 100, H = 30;
  const min = Math.min(...values), max = Math.max(...values);
  const span = (max - min) || 1;
  const step = W / (values.length - 1);
  const pts = values.map((v, i) => `${(i * step).toFixed(2)},${(H - ((v - min) / span) * H).toFixed(2)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" width="100%" height="100%" style={{ display: "block" }}>
      <polyline fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke"
        strokeLinejoin="round" strokeLinecap="round" points={pts} />
    </svg>
  );
}

// Scroll a board card into view + briefly highlight it (jump-to-card from the summary).
function jumpToVisual(id) {
  if (!id) return;
  const el = document.getElementById(`visual-${id}`);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.style.transition = "box-shadow .2s";
  el.style.boxShadow = "0 0 0 3px #c7d2fe";
  setTimeout(() => { el.style.boxShadow = ""; }, 1400);
}

// Executive summary — two REAL, grounded segments (roadmap #77):
//  • Key drivers   = the deterministic/LLM brief (each item backed by a visual → click to jump).
//  • Anomalies & risks = board.anomalies (deterministic outlier / period-over-period movers).
// "Recommended actions" is intentionally NOT shown: there is no grounded data source for it yet
// (would need a new backend narrative step) — we don't fabricate prescriptive advice for a CFO.
function Brief({ items, anomalies, visuals }) {
  const drivers = items || [];
  const risks = anomalies || [];
  if (drivers.length === 0 && risks.length === 0) return null;
  const byId = (id) => (visuals || []).find(v => v.id === id);
  const titleOf = (id) => byId(id)?.title || "";
  const anomIds = new Set((risks || []).map(a => a.visualId).filter(Boolean));
  // Per-bullet takeaway badge — ONLY from a REAL signal: the backing KPI's goal-based tintState,
  // or membership in the deterministic anomaly set. No signal → no badge (never invented).
  const driverBadge = (id) => {
    const t = byId(id)?.tintState;
    const b = t === "good" ? { c: "#059669", bg: "#ecfdf5", bd: "#a7f3d0", label: "▲ On track" }
      : t === "bad" ? { c: "#e11d48", bg: "#fef2f4", bd: "#fecdd3", label: "▼ Needs attention" }
      : anomIds.has(id) ? { c: "#b45309", bg: "#fff7ed", bd: "#fed7aa", label: "Watch" }
      : null;
    if (!b) return null;
    return <span style={{ marginRight: 8, fontSize: 10, fontWeight: 800, color: b.c, background: b.bg, border: `1px solid ${b.bd}`, borderRadius: 999, padding: "1px 8px", whiteSpace: "nowrap" }}>{b.label}</span>;
  };
  const seg = { fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 8 };
  const jumpChip = (id) => id ? (
    <button onClick={() => jumpToVisual(id)} title={`Jump to “${titleOf(id)}”`}
      style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 700, color: "#4f46e5", background: "#eef2ff", border: "1px solid #e0e7ff", borderRadius: 999, padding: "1px 8px", cursor: "pointer", whiteSpace: "nowrap" }}>
      {titleOf(id) ? `${titleOf(id)} →` : "View →"}
    </button>
  ) : null;
  return (
    <div style={{ ...card, background: "linear-gradient(180deg,#f8faff,#fff)", borderColor: "#e0e7ff" }}>
      <div style={{ display: "grid", gridTemplateColumns: risks.length ? "1.6fr 1fr" : "1fr", gap: 20, alignItems: "start" }}>
        {drivers.length > 0 && (
          <div>
            <div style={{ ...seg, color: "#6366f1" }}>Key performance drivers</div>
            <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 7 }}>
              {drivers.map((it, i) => (
                <li key={i} style={{ fontSize: 13, color: "#1e293b", lineHeight: 1.6 }}>
                  {driverBadge(it.backingVisualId)}{it.text}{jumpChip(it.backingVisualId)}
                </li>
              ))}
            </ul>
          </div>
        )}
        {risks.length > 0 && (
          <div style={{ borderLeft: drivers.length ? "1px solid #e5e9f5" : "none", paddingLeft: drivers.length ? 20 : 0 }}>
            <div style={{ ...seg, color: "#b45309" }}>Anomalies &amp; risks</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {risks.map((a, i) => (
                <button key={i} onClick={() => jumpToVisual(a.visualId)} title={a.visualId ? `Jump to “${titleOf(a.visualId)}”` : ""}
                  style={{ textAlign: "left", display: "flex", alignItems: "flex-start", gap: 8, background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 10, padding: "7px 10px", cursor: a.visualId ? "pointer" : "default" }}>
                  <span style={{ fontSize: 13, flexShrink: 0 }}>{a.kind === "pop" ? "📈" : "⚠️"}</span>
                  <span style={{ fontSize: 12, color: "#7c2d12", lineHeight: 1.45 }}>
                    <b>{titleOf(a.visualId) || a.metric}</b>{a.label ? ` — ${a.label}` : ""}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AuditDrawer({ visual, onClose }) {
  if (!visual) return null;
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.35)", zIndex: 1000, display: "flex", justifyContent: "flex-end" }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(560px,92vw)", height: "100%", background: "#fff", padding: 24, overflow: "auto", boxShadow: "-8px 0 24px rgba(0,0,0,.12)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <span style={{ fontSize: 15, fontWeight: 700 }}>How this was computed</span>
          <button onClick={onClose} style={{ border: "none", background: "none", fontSize: 20, cursor: "pointer", color: "#64748b" }}>×</button>
        </div>
        <Field label="Visual">{visual.title}</Field>
        {visual.formula_text && <Field label="Formula">{visual.formula_text}</Field>}
        <Field label="Tables referenced">{(visual.tables || []).join(", ") || "—"}</Field>
        <Field label="Columns">{(visual.columns || []).join(", ") || "—"}</Field>
        <Field label="Query (read-only)"><pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 11, background: "#0f172a", color: "#e2e8f0", padding: 12, borderRadius: 8, margin: 0 }}>{visual.sql || "—"}</pre></Field>
        {visual.result_set && visual.result_set.length > 0 && (
          <Field label={`Source rows (${visual.result_set.length})`}>
            <div style={{ maxHeight: 240, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 8 }}>
              <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                <thead><tr>{Object.keys(visual.result_set[0]).map(k => <th key={k} style={{ textAlign: "left", padding: "6px 8px", position: "sticky", top: 0, background: "#f1f5f9", borderBottom: "1px solid #e2e8f0" }}>{k}</th>)}</tr></thead>
                <tbody>{visual.result_set.slice(0, 50).map((r, i) => <tr key={i}>{Object.keys(visual.result_set[0]).map(k => <td key={k} style={{ padding: "5px 8px", borderBottom: "1px solid #f1f5f9" }}>{String(r[k])}</td>)}</tr>)}</tbody>
              </table>
            </div>
          </Field>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, color: "#1e293b" }}>{children}</div>
    </div>
  );
}

// NOTE (fixed-visual-blueprint R21, relocated 2026-09-04): the single-entry-point banner/
// indicator (`TemplateBanner`, formerly here) moved to `DashboardPage.jsx`'s control bar as
// `TemplateControl` + a corner `ImportActivityPill` — see that file. Analyze no longer renders
// any template-entry-point UI; it keeps the two-section board (below) so a published Blueprint
// still renders correctly here, just with no way to LAUNCH the import from this page.

// ─── Main board ─────────────────────────────────────────────────────────────────────────────
export default function AutoDeck({ projectId, onDrill, refreshSignal, regenerateSignal, onThemeChange, brandColorsOn, onPinnedChange,
  chromeless = false, filter = "all", onBoardMeta, onBusyChange }) {
  // chromeless: hide AutoDeck's own sticky control bar (the Dashboard page owns the chrome).
  // filter: "all" | "pinned" | "auto" — restrict which cards render (Dashboard's segmented filter).
  // onBoardMeta: report {status, total, pinned, auto} up so a parent topbar can show the cap/counts.
  // regenerateSignal: bump → run a full force-regenerate (same as the "Regenerate board" button),
  //   so a chromeless parent's "Refresh" truly rebuilds instead of re-reading the cache. Both the
  //   Dashboard "Refresh" and the Analyze "Regenerate board" button show the same spinning
  //   RefreshCw while busy.
  // onBusyChange: report working state up (POST in flight OR board still streaming) so a
  //   chromeless parent can show its own progress indicator (AutoDeck's bar is hidden then).
  const [board, setBoard] = useState(null);   // {status, domain, visuals, brief, ...}
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [domainOpts, setDomainOpts] = useState(null);   // available analysis lenses (lazy-loaded)
  const [domainPickerOpen, setDomainPickerOpen] = useState(false);
  const [theme, setTheme] = useState({ enabled: false, palette: [], roles: {} }); // brand colors (R22/R23)
  const [themeOpen, setThemeOpen] = useState(false);
  const pollRef = useRef(null);
  const mounted = useRef(true);
  const retryRef = useRef(0);   // consecutive transient /ai/board failures during the build

  // Effective series palette + role colors: the customer's brand colors when enabled, else the
  // Boomerang default palette (roles apply only when a brand theme is active, R23).
  const effPalette = (theme.enabled && theme.palette && theme.palette.length) ? theme.palette : PALETTE;
  const effRoles = theme.enabled ? (theme.roles || {}) : {};

  const { working: boardActivity, beginWork } = useBoardActivity();

  // Is THIS board actually producing (status generating OR any visual still pending)? Drives the
  // handoff to the provider's poll. Excludes `busy` so a non-generation action (export) doesn't
  // start the board spinner.
  const boardGenerating = board?.status === "generating"
    || (Array.isArray(board?.visuals) && board.visuals.some((v) => v.status === "pending"));
  // The AUTHORITATIVE spinner state is the App-level provider (`boardActivity[projectId]`), which
  // runs its OWN poll and survives section/window switches. OR with local `busy` for instant
  // feedback on the acting instance.
  const spinning = busy || boardGenerating || !!(projectId && boardActivity[projectId]);

  const call = useCallback(async (path, method = "GET", body) => {
    let token = await getFreshToken();
    const opts = { method, headers: { Authorization: `Bearer ${token}`, "X-Project-Id": projectId } };
    if (body) { opts.headers["Content-Type"] = "application/json"; opts.body = JSON.stringify(body); }
    let res = await fetch(`${API_URL}${path}`, opts);
    if (res.status === 401) { token = await getFreshToken(); opts.headers.Authorization = `Bearer ${token}`; res = await fetch(`${API_URL}${path}`, opts); }
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  }, [projectId]);

  const refresh = useCallback(async () => {
    // Cancel any pending scheduled poll before (re)fetching so the visibility/interval backstops
    // can't stack multiple setTimeout chains (poll storm) — a single chain survives at a time.
    if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
    try {
      const data = await call("/ai/board");
      if (!mounted.current) return;
      retryRef.current = 0;   // a successful read clears the transient-failure streak
      if (!data.enabled) { setBoard({ status: "disabled" }); setLoading(false); if (onPinnedChange) onPinnedChange([]); if (onBoardMeta) onBoardMeta({ status: "disabled", total: 0, pinned: 0, auto: 0 }); return; }
      setBoard(data.board);
      setError(null);
      setLoading(false);
      // Report the currently-pinned ("Add to board") visuals up so the chat can show an accurate
      // "✓ Added" state + a Remove toggle (derived from the persisted board, not session state).
      if (onPinnedChange) {
        onPinnedChange((data.board && data.board.visuals ? data.board.visuals : [])
          .filter((v) => v.kind === "custom" || v.custom)
          .map((v) => ({ id: v.id, title: v.title, chart_spec: v.chart_spec })));
      }
      // Report board meta up so a parent topbar (Dashboard page, #77) can show the cap + counts.
      // Count only what actually RENDERS: drop removed cards and hidden failed AUTO cards so the
      // "X / cap" badge matches the visible board (a failed pinned/custom card still counts).
      if (onBoardMeta) {
        const vs = (data.board && data.board.visuals) ? data.board.visuals : [];
        const c = (data.board && data.board.curation) || {};
        const pinnedIds = new Set(c.pinned || []);
        const removedIds = new Set(c.removed || []);
        const isPinned = (v) => v.custom || v.kind === "custom" || pinnedIds.has(v.id);
        const visibleVs = vs.filter((v) => !removedIds.has(v.id) && (v.status !== "failed" || isPinned(v)));
        const pinned = visibleVs.filter(isPinned).length;
        // Pinned Tables (blueprint-tables-and-concepts) render as entries in this SAME
        // `board.visuals` array as charts (blueprint_render._build's deliberate choice), but a
        // Template's "Extracted N charts" count in the wizard (ImportWizard.jsx) — and the CAP
        // badge here — is chart-only. Without splitting them out, publishing 18 charts + 1 table
        // showed "19 / 18 cards" on the Dashboard, reading as a mismatched/duplicated count bug
        // rather than what it actually is: 18 charts (matching the wizard) PLUS 1 separately-
        // published table. Report tables separately so the badge can say "18 / 18 charts · 1
        // table" instead of lumping them into one number the wizard's own count never promised.
        const isTableKind = (v) => v.kind === "grid_table" || v.kind === "stat_card_table";
        const tableCount = visibleVs.filter(isTableKind).length;
        const chartVs = visibleVs.filter((v) => !isTableKind(v));
        onBoardMeta({ status: data.board ? data.board.status : "disabled",
                     total: chartVs.length, pinned, auto: chartVs.length - pinned,
                     tables: tableCount });
      }
      // Keep polling while the board is still streaming visuals in.
      if (data.board && data.board.status === "generating") {
        pollRef.current = setTimeout(refresh, 2500);
      }
    } catch (e) {
      if (!mounted.current) return;
      // The FIRST /ai/board triggers server-side generation; a large project (many tables) can
      // make that first call slow enough to time out at the gateway, OR a poll can hit a
      // transient blip. The board is still being built and PERSISTED server-side, so a follow-up
      // poll succeeds. Keep the friendly "building" state and retry silently for a while instead
      // of flashing a scary "failed" error the moment the first request is slow. Only after a
      // sustained streak of failures do we surface a soft, recoverable message.
      retryRef.current += 1;
      if (retryRef.current <= MAX_BUILD_RETRIES) {
        setError(null);
        setLoading(true);   // stay on the "Building your board…" skeleton
        pollRef.current = setTimeout(refresh, 3000);
      } else {
        setError("Your board is taking longer than usual to build.");
        setLoading(false);
      }
    }
  }, [call, onPinnedChange, onBoardMeta]);

  useEffect(() => {
    mounted.current = true;
    retryRef.current = 0;
    setLoading(true); setBoard(null); setError(null);
    refresh();
    return () => { mounted.current = false; if (pollRef.current) clearTimeout(pollRef.current); };
  }, [refresh, projectId]);

  // Resilience (roadmap #83): a backgrounded tab THROTTLES/FREEZES the setTimeout poll chain, so
  // a board that's still generating can look stuck (spinner frozen, "processing" cards never
  // resolve) when you come back. Re-fetch the moment the tab regains visibility/focus — this
  // reschedules the fast poll if the board is still streaming, or lands the finished board.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible" && mounted.current) refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [refresh]);

  // Interval backstop while generating: an independent 5s re-poll (only when visible) so the
  // board can't get stuck if the setTimeout chain is ever dropped (throttling, a transient error).
  useEffect(() => {
    if (board?.status !== "generating") return undefined;
    const id = setInterval(() => {
      if (mounted.current && document.visibilityState === "visible") refresh();
    }, 5000);
    return () => clearInterval(id);
  }, [board?.status, refresh]);

  // Apply a resolved theme locally AND notify the parent so the chat charts (RenderChart) use
  // the same brand palette as the board — passing null when disabled (→ chat default palette).
  const applyTheme = useCallback((t) => {
    const next = { enabled: !!(t && t.enabled), palette: (t && t.palette) || [],
                   roles: (t && (t.resolvedRoles || t.roles)) || {} };
    setTheme(next);
    if (onThemeChange) onThemeChange(next.enabled && next.palette.length ? next.palette : null);
  }, [onThemeChange]);

  // Brand palette is per-customer (not per-project) — fetch once when the board mounts, only
  // when the brand-colors flag is on (otherwise charts stay on the default palette).
  useEffect(() => {
    if (!brandColorsOn) return;
    let cancelled = false;
    (async () => {
      try {
        const d = await call("/ai/board/theme");
        if (!cancelled && d.enabled && d.theme) applyTheme(d.theme);
      } catch (e) { /* keep default */ }
    })();
    return () => { cancelled = true; };
  }, [call, applyTheme, brandColorsOn]);

  const saveTheme = async (palette, { enabled = true, source = "manual" } = {}) => {
    try {
      const d = await call("/ai/board/theme", "PUT", { palette, enabled, source });
      if (d.enabled && d.theme) applyTheme(d.theme);
    } catch (e) { setError("Couldn't save brand colors."); }
  };
  const resetTheme = () => saveTheme([], { enabled: false });

  // NOTE (fixed-visual-blueprint R21, relocated 2026-09-04): board-mode fetch + switchMode moved
  // to DashboardPage.jsx (the new entry point owns this state now). AutoDeck still reads
  // `board.boardMode` (from the board API response itself, not local state) for the two-section
  // labels below — that's unaffected by the relocation.

  const regenerateAll = async () => {
    beginWork(projectId);   // optimistic: spinner on immediately, survives a section switch
    setBusy(true);
    try {
      const d = await call("/ai/board/generate", "POST");
      if (d.enabled) { setBoard(d.board); if (d.board.status === "generating") pollRef.current = setTimeout(refresh, 2500); }
    }
    catch (e) {
      // Real bug reported live (a large template publish, 18-19 charts): this call throwing
      // (a slow response, a gateway hiccup on the wire) used to jump STRAIGHT to a terminal
      // "Regeneration failed" error state — but `board.generate_board` persists a "generating"
      // board server-side BEFORE it starts computing visuals (board.py's `_prepare_board`/
      // `put_board`, ahead of `_advance`), so the work is very likely still proceeding on the
      // backend even when THIS request itself errored on the wire. Falling straight to an error
      // both under-sold what was actually happening (the board usually WAS still building) and
      // meant the "Keep waiting" retry a customer clicked next just repeated the same race.
      // Poll instead of failing immediately — `refresh()`'s own retry streak (MAX_BUILD_RETRIES)
      // is the real backstop if the board genuinely never resolves.
      retryRef.current = 0;
      pollRef.current = setTimeout(refresh, 2000);
    } finally { setBusy(false); }
  };
  const regenerateVisual = async (id) => {
    try { const d = await call(`/ai/board/visual/${encodeURIComponent(id)}/regenerate`, "POST"); if (d.enabled) setBoard(d.board); } catch (e) { /* keep prior */ }
  };
  // Auto-Insights on/off (roadmap #181): flip the per-project setting and swap in the board the
  // server returns in the same call — OFF comes back status="off" (no generation), ON rebuilds
  // (force). Mirrors regenerateAll's call→setBoard→poll-if-generating shape. Pinned/template
  // content is untouched server-side; this only pauses/resumes the AI auto-board.
  const toggleAutoInsights = async () => {
    const next = (board && board.autoInsights === "off") ? "on" : "off";
    setBusy(true); setError(null);
    try {
      const d = await call("/ai/board/auto-insights", "PUT", { value: next });
      if (d && d.enabled) {
        setBoard(d.board);
        if (d.board && d.board.status === "generating") pollRef.current = setTimeout(refresh, 2500);
      }
    } catch (e) { setError("Couldn't change Auto-Insights."); }
    finally { setBusy(false); }
  };
  const exportPptx = async () => {
    setBusy(true);
    try {
      let token = await getFreshToken();
      const res = await fetch(`${API_URL}/ai/board/export.pptx`, { headers: { Authorization: `Bearer ${token}`, "X-Project-Id": projectId } });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `boomerang-board-${projectId}.pptx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { setError("Couldn't export the deck."); } finally { setBusy(false); }
  };
  // Living/share link removed for now (product decision): no public sharing, and the in-app
  // read-only route was never wired. Re-add here if a snapshot-based share is built later.
  const openDomainPicker = async () => {
    setDomainPickerOpen(true);
    if (domainOpts === null) {
      try { const d = await call("/ai/board/domains"); setDomainOpts(d.enabled ? (d.domains || []) : []); }
      catch (e) { setDomainOpts([]); }
    }
  };
  const selectDomain = async (key) => {
    setDomainPickerOpen(false);
    if (!key || key === board?.domain) return;
    setBusy(true);
    try { const d = await call("/ai/board/domain", "PUT", { domain: key }); if (d.enabled) { setBoard(d.board); if (d.board.status === "generating") pollRef.current = setTimeout(refresh, 2500); } }
    catch (e) { setError("Couldn't change the domain."); } finally { setBusy(false); }
  };

  // Curation (R6.3): pin/remove persist on the data-version-keyed board and are applied here.
  const curate = async (next) => {
    try { const d = await call("/ai/board/curate", "POST", next); if (d.enabled) setBoard(d.board); } catch (e) { /* keep prior */ }
  };
  const cur = board?.curation || { pinned: [], order: [], removed: [] };
  const togglePin = (id) => {
    const pinned = cur.pinned.includes(id) ? cur.pinned.filter(x => x !== id) : [...cur.pinned, id];
    curate({ pinned, order: cur.order, removed: cur.removed });
  };
  const deleteCustom = async (id) => {
    try { await call(`/ai/board/custom/${encodeURIComponent(id)}`, "DELETE"); } catch (e) { /* ignore */ }
    refresh();
  };
  const removeVisual = (id) => {
    const v = (board?.visuals || []).find(x => x.id === id);
    if (v && (v.custom || v.kind === "custom")) { deleteCustom(id); return; }   // user-authored → delete
    curate({ pinned: cur.pinned.filter(x => x !== id), order: cur.order, removed: [...new Set([...cur.removed, id])] });
  };

  // Refetch when the parent signals a new custom visual was added (no skeleton reset).
  const firstSignal = useRef(true);
  useEffect(() => {
    if (firstSignal.current) { firstSignal.current = false; return; }
    refresh();
  }, [refreshSignal]);  // eslint-disable-line react-hooks/exhaustive-deps

  // A chromeless parent's "Refresh" bumps regenerateSignal → run the SAME force-regenerate as the
  // "Regenerate board" button (POST /ai/board/generate, force=True) so a stale board is actually
  // rebuilt from the latest data, not just re-read from cache. Skip the initial mount.
  const firstRegen = useRef(true);
  useEffect(() => {
    if (firstRegen.current) { firstRegen.current = false; return; }
    regenerateAll();
  }, [regenerateSignal]);  // eslint-disable-line react-hooks/exhaustive-deps

  // When this instance observes the board actually generating, hand off to the App-level provider's
  // own poll loop — which then owns the truth and the CLEARING of the flag independently of this
  // component's lifecycle. (Clicks also call beginWork directly, before the board reflects it.)
  useEffect(() => {
    if (boardGenerating) beginWork(projectId);
  }, [boardGenerating, projectId, beginWork]);

  // The control bar (domain badge + Brand colors + Export + Regenerate) is rendered in EVERY
  // non-disabled state — loading, generating, empty, and ready — so the buttons are stable and
  // never "pop in" late once the board finishes (the earlier UX bug: the toolbar only existed
  // inside the fully-loaded board view). Export is disabled until there's a real board.
  const controlBar = () => {
    if (chromeless) return null;   // Dashboard page renders its own topbar (#77)
    const canExport = !!board && board.status !== "empty";
    // Blueprint mode has no switchable "domain" — its content is fixed by whatever template was
    // imported (board.domain is hardcoded "blueprint" in blueprint_render.py), so the domain
    // picker doesn't apply here. Historically this duplicated the old Analyze mode indicator
    // ("Analyzing as: Blueprint" next to "📊 Template Active" said the same thing twice); the
    // indicator has since moved to DashboardPage.jsx (2026-09-04), but the domain picker is still
    // hidden here on principle — it's meaningless for a Blueprint board regardless of page. AND the
    // picker's "change" action calls PUT /ai/board/domain, which regenerates in AI mode without
    // preserving boardMode — silently kicking a Blueprint board back to AI. Hide it in this mode
    // rather than have it double up or trigger that path; Brand colors/Export/Regenerate are
    // unaffected and still apply to a Blueprint board.
    const isBlueprintBoard = board && board.domain === "blueprint";
    return (
      // Sticky toolbar card: pins to the top of the board's scroll area so the domain badge +
      // Brand colors / Export / Regenerate stay reachable while scrolling the board. Uses a soft
      // brand-tinted fill (not white) so it stays visible against the WHITE visual/text cards it
      // floats over while scrolling — a white bar blended in and got lost (pilot feedback, 21.6).
      // The white Export pill and the lighter indigo pills both read against this tint; the
      // stronger shadow + border reinforce the separation over both the dark header and the cards.
      <div style={{
        position: "sticky", top: 0, zIndex: 30,
        display: "flex", flexDirection: "column", gap: 8,
        background: "#e8edfb", border: "1px solid #c7d2fe", borderRadius: 12,
        padding: "8px 12px", boxShadow: "0 8px 22px rgba(15,23,42,0.16)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          {isBlueprintBoard ? (
            // Empty spacer (not the domain picker) — keeps Brand colors/Export/Regenerate
            // right-aligned via space-between without a single leftover flex child collapsing them left.
            <span />
          ) : board && board.domain ? (
            <DomainBadge domain={board.domain} confidence={board.domainConfidence} confirmedBy={board.domainConfirmedBy}
              options={domainOpts} open={domainPickerOpen} onOpen={openDomainPicker}
              onSelect={selectDomain} onClose={() => setDomainPickerOpen(false)} />
          ) : (
            <span style={{ fontSize: 12, color: "#94a3b8" }}>Analyzing your data…</span>
          )}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {/* NOTE (fixed-visual-blueprint R21): the earlier "Smart (AI) | My layout" toolbar
                toggle lived here, then a single-entry-point banner/indicator above this control
                bar (`TemplateBanner`) — both superseded. The mode switch now lives entirely in
                DashboardPage.jsx's `TemplateControl` (relocated 2026-09-04); this page never
                renders a mode-switch control at all, since users don't think about a "template"
                until after seeing the AI board (R21.1) and Dashboard is the intended home. */}
            {brandColorsOn && (
              <button disabled={busy} onClick={() => setThemeOpen(o => !o)} title="Set your brand colors for the charts" style={{ fontSize: 12, padding: "6px 12px", borderRadius: 999, border: `1px solid ${theme.enabled ? "#c7d2fe" : "#e2e8f0"}`, background: theme.enabled ? "#eef2ff" : "#fff", color: "#334155", cursor: "pointer", fontWeight: 600 }}>🎨 Brand colors{theme.enabled ? " ✓" : ""}</button>
            )}
            <button disabled={busy || !canExport} onClick={exportPptx} title={canExport ? "Download an editable PowerPoint of this board" : "Available once the board has finished generating"} style={{ fontSize: 12, padding: "6px 12px", borderRadius: 999, border: "1px solid #e2e8f0", background: "#fff", color: "#334155", cursor: (busy || !canExport) ? "default" : "pointer", fontWeight: 600, opacity: (busy || !canExport) ? 0.55 : 1 }}>⬇ Export deck (PPTX)</button>
            {/* Auto-Insights on/off (roadmap #181) — pause/resume the AI auto-board. Hidden in
                blueprint mode (the template board isn't the AI auto-board this governs). When OFF,
                the board comes back status="off" and no Bedrock/Athena work runs. Only shown once
                we know the current state (board.autoInsights present). */}
            {!isBlueprintBoard && board && board.autoInsights && (
              <button disabled={busy || spinning} onClick={toggleAutoInsights}
                title={board.autoInsights === "off"
                  ? "Auto-Insights is off — turn it on to auto-generate this board"
                  : "Pause auto-generating this board (your pinned charts and template stay)"}
                style={{ fontSize: 12, padding: "6px 12px", borderRadius: 999,
                  border: `1px solid ${board.autoInsights === "off" ? "#e2e8f0" : "#c7d2fe"}`,
                  background: board.autoInsights === "off" ? "#fff" : "#eef2ff", color: "#334155",
                  cursor: (busy || spinning) ? "default" : "pointer", fontWeight: 600 }}>
                ✨ Auto-Insights {board.autoInsights === "off" ? "Off" : "On"}
              </button>
            )}
            <button disabled={spinning} onClick={regenerateAll} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, padding: "6px 12px", borderRadius: 999, border: "1px solid #e0e7ff", background: "#eef2ff", color: "#4f46e5", cursor: spinning ? "default" : "pointer", fontWeight: 600 }}>
              <RefreshCw size={13} className={spinning ? "animate-spin" : undefined} /> {spinning ? "Working…" : "Regenerate board"}
            </button>
          </div>
        </div>
        {/* Non-blocking mode notice (e.g. blueprint mode on but nothing published yet, R1.4) is
            surfaced as a toast from DashboardPage.jsx's switchBoardMode now (relocated 2026-09-04
            with the rest of the mode-switch control). */}
        {themeOpen && <BrandColorsPanel theme={theme} defaultPalette={PALETTE} onSave={saveTheme} onReset={resetTheme} onClose={() => setThemeOpen(false)} />}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {controlBar()}
        {/* Friendly, intentional progress copy so a slow first build reads as "working," never
            "failed." The skeleton + this caption stay up while we poll the board into existence. */}
        <div style={{ fontSize: 13, color: "#6366f1", textAlign: "center", fontWeight: 600 }}>
          ✨ Building your executive board from your data…
          <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 400, marginTop: 3 }}>
            This can take up to a minute the first time. Your charts will appear here automatically.
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 14 }}><Skeleton /><Skeleton /><div style={{ gridColumn: "span 2" }}><Skeleton /></div></div>
      </div>
    );
  }
  if (board?.status === "disabled") return null;
  if (error) return (
    <div style={{ ...card, color: "#475569", textAlign: "center", padding: "28px 16px" }}>
      <div style={{ maxWidth: 460, margin: "0 auto", lineHeight: 1.5 }}>{error} It's still finishing in the background.</div>
      <button onClick={() => { retryRef.current = 0; setError(null); setLoading(true); refresh(); }}
        style={{ marginTop: 12, color: "#fff", background: "#4f46e5", border: "none", borderRadius: 8, padding: "8px 18px", cursor: "pointer", fontWeight: 600 }}>Keep waiting</button>
    </div>
  );
  if (!board) return null;

  if (board.status === "off") {
    // Auto-Insights is turned OFF for this project (roadmap #181) — the AI auto-board did not
    // generate (no Bedrock/Athena work). Show a calm, intentional state with a one-click "Generate
    // insights" that flips it back on and builds the board. Pinned/template content is unaffected
    // and rendered by its own sections elsewhere; this branch only replaces the auto-board area.
    const turnOn = async () => {
      setBusy(true); setError(null);
      try {
        const d = await call("/ai/board/auto-insights", "PUT", { value: "on" });
        if (d && d.enabled) {
          setBoard(d.board);
          if (d.board && d.board.status === "generating") pollRef.current = setTimeout(refresh, 2500);
        }
      } catch (e) { setError("Couldn't turn Auto-Insights on."); }
      finally { setBusy(false); }
    };
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {controlBar()}
        <div style={{ ...card, color: "#475569", textAlign: "center", padding: "36px 16px",
                      display: "flex", flexDirection: "column", gap: 14, alignItems: "center" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Auto-Insights is off for this project</div>
          <div style={{ maxWidth: 520, lineHeight: 1.5, fontSize: 13.5 }}>
            Your dashboard isn't auto-generating charts. Your pinned charts and any published template
            stay exactly as they are — this only pauses the AI auto-board. Ask a question any time, or
            turn Auto-Insights back on to rebuild the board from your current data.
          </div>
          <button onClick={turnOn} disabled={busy} style={{
            marginTop: 2, color: "#fff",
            background: busy ? "#a5b4fc" : "linear-gradient(135deg, #6366f1, #7c3aed)",
            border: "none", borderRadius: 10, padding: "10px 20px", cursor: busy ? "default" : "pointer",
            fontWeight: 700, fontSize: 13 }}>
            {busy ? "Generating…" : "✨ Generate insights"}
          </button>
        </div>
      </div>
    );
  }

  if (board.status === "empty") {
    // Distinguish a genuine data-shape gap from a transient build failure, and always offer a
    // way out (Regenerate) so a bad run can't leave the board permanently stuck.
    const buildFailed = board.emptyReason === "all_visuals_failed";
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {controlBar()}
        <div style={{ ...card, color: "#64748b", textAlign: "center", padding: "36px 16px", display: "flex", flexDirection: "column", gap: 14, alignItems: "center" }}>
          <div style={{ maxWidth: 520, lineHeight: 1.5 }}>
            {buildFailed
              ? "We couldn't build the visuals for this project just now. This is usually temporary — please regenerate."
              : "Not enough structure in this project's data to auto-visualize yet. Upload data with at least one measure (e.g. a value/amount) and one dimension (e.g. a category or date)."}
          </div>
        </div>
      </div>
    );
  }

  // Apply curation: hide removed, pinned first (stable within groups).
  const pinnedSet = new Set(cur.pinned || []);
  const removedSet = new Set(cur.removed || []);
  const visuals = (board.visuals || []).filter(v => !removedSet.has(v.id));
  visuals.sort((a, b) => (pinnedSet.has(b.id) ? 1 : 0) - (pinnedSet.has(a.id) ? 1 : 0));
  // Dashboard segmented filter (#77): "pinned" = user-authored (Add-to-board) or pin-to-top;
  // "auto" = generated cards; "all" = everything. Default "all" is unchanged behavior.
  const _isPinnedCard = (v) => v.custom || v.kind === "custom" || pinnedSet.has(v.id);
  // Fully HIDE auto visuals that couldn't be generated ("Needs data") — an auto board should only
  // show cards that actually built (owner decision). A user's PINNED/custom card that fails still
  // shows its "Needs data" state so they know a saved card needs attention.
  const buildable = visuals.filter(v => v.status !== "failed" || _isPinnedCard(v));
  const shown = filter === "pinned" ? buildable.filter(_isPinnedCard)
              : filter === "auto" ? buildable.filter(v => !_isPinnedCard(v))
              : buildable;

  // Real UX suggestion: "when refreshing the dashboard should we wipe immediately... indicating
  // it is in process of refreshing?" Confirmed gap: `regenerateAll()` (Dashboard's "Refresh" /
  // Analyze's "Regenerate board") leaves the OLD `board` fully rendered and INTERACTIVE while
  // the new one builds server-side — only a small toolbar icon spins, so a customer reading the
  // board mid-refresh has no way to tell the numbers on screen are about to be replaced (they
  // could easily read stale data as current). `spinning` is the SAME authoritative "board is
  // (re)building" signal the toolbar's own spin icon already uses; `loading` is the SEPARATE
  // very-first-build state (its own full skeleton already exists above) — dim only a REFRESH of
  // an already-rendered board, never double up with the first-load skeleton. Scoped to the data
  // content only (pins notice, brief, cards) — NOT the Scenario/Goals tools panel or the audit
  // drawer, which aren't stale data and stay fully usable during a refresh.
  const refreshingExisting = spinning && !loading && !!board && board.status !== "empty";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Single entry point (R21) — relocated to DashboardPage.jsx's control bar (2026-09-04);
          Analyze no longer renders a template banner/indicator here at all. */}
      {controlBar()}

      {refreshingExisting && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center",
          fontSize: 12.5, fontWeight: 600, color: "#4f46e5", background: "#eef2ff",
          border: "1px solid #c7d2fe", borderRadius: 8, padding: "8px 12px" }}>
          <RefreshCw size={13} className="animate-spin" /> Refreshing with your latest data — the cards below are the previous version until this finishes.
        </div>
      )}

      <div style={refreshingExisting
        ? { opacity: 0.45, pointerEvents: "none", filter: "grayscale(20%)", transition: "opacity .15s ease" }
        : undefined}>
        {/* Durable pins (task 26): notice when the data-version changed and pinned charts were
            carried forward / couldn't be refreshed. */}
        {(board.pinsCarried || board.pinsNeedAttention) ? (
          <div style={{ ...card, padding: "10px 14px", fontSize: 12.5, color: "#334155",
            background: board.pinsNeedAttention ? "#fff7ed" : "#eef2ff",
            borderColor: board.pinsNeedAttention ? "#fed7aa" : "#c7d2fe" }}>
            {board.pinsCarried ? `Carried ${board.pinsCarried} pinned chart${board.pinsCarried > 1 ? "s" : ""} forward and refreshed for your updated data. ` : ""}
            {board.pinsNeedAttention ? `${board.pinsNeedAttention} pinned chart${board.pinsNeedAttention > 1 ? "s" : ""} couldn't be refreshed for the new data — re-add ${board.pinsNeedAttention > 1 ? "them" : "it"} from a chat answer.` : ""}
          </div>
        ) : null}

        <Brief items={board.brief} anomalies={board.anomalies} visuals={board.visuals} />

        {shown.length === 0 && (
          <div style={{ ...card, color: "#94a3b8", textAlign: "center", padding: "24px 16px", fontSize: 13 }}>
            No {filter === "pinned" ? "pinned" : filter === "auto" ? "auto-generated" : ""} cards to show.
          </div>
        )}

        {/* Two-section rendering (fixed-visual-blueprint R21.4-R21.7, Task 26): the AI-selected or
            Blueprint-selected cards (label depends on boardMode) ALWAYS separate from the user's
            own pinned/custom cards ("My Additions") — regardless of mode, so publishing/switching a
            template only ever touches the first section (R21.5/R21.6). When the segmented filter
            (Dashboard's "pinned"/"auto") already narrows to one group, skip the section labels
            entirely — they'd be redundant/misleading with only one group visible. */}
        {filter === "all" ? (
          <TwoSectionGrid shown={shown} isPinnedCard={_isPinnedCard} boardMode={board.boardMode}
            chromeless={chromeless} onAudit={setAudit} onRegenerate={regenerateVisual}
            onDrill={onDrill} onPin={togglePin} onRemove={removeVisual} pinnedSet={pinnedSet}
            palette={effPalette} roles={effRoles} />
        ) : (
          <div className={`autodeck-grid${chromeless ? " autodeck-grid-wide" : ""}`}>
            {shown.map(v => (
              <VisualCard key={v.id} visual={v} onAudit={setAudit} onRegenerate={regenerateVisual}
                onDrill={onDrill} onPin={togglePin} onRemove={removeVisual} isPinned={pinnedSet.has(v.id)}
                palette={effPalette} roles={effRoles} />
            ))}
          </div>
        )}

        {board.status === "generating" && <div style={{ fontSize: 12, color: "#6366f1", textAlign: "center" }}>Generating more visuals…</div>}
      </div>

      {/* Scenario & Goals tools: kept on Analyze only. Removed from the Dashboard (chromeless)
          per product decision — the board is a read/curate/export surface there. */}
      {!chromeless && <ToolsPanel call={call} />}

      <AuditDrawer visual={audit} onClose={() => setAudit(null)} />
      {/* Import wizard (task 27) relocated to DashboardPage.jsx — launched only from
          TemplateControl there (R21.1), never from Analyze. */}
    </div>
  );
}

// ─── Scenario (Rung-1) + Goals editors (R17 / R19) ──────────────────────────────────────────
function ToolsPanel({ call }) {
  const [open, setOpen] = useState(false);
  const [scn, setScn] = useState({ ratio: "", ratio_label: "ROAS", delta: "", delta_label: "spend", output_label: "revenue" });
  const [scnOut, setScnOut] = useState(null);
  const [goals, setGoals] = useState(null);
  const [goalForm, setGoalForm] = useState({ metric_key: "", target: "" });

  const runScenario = async () => {
    try {
      const d = await call("/ai/board/scenario", "POST", {
        ratio: scn.ratio === "" ? null : parseFloat(scn.ratio),
        ratio_label: scn.ratio_label, delta: parseFloat(scn.delta || "0"),
        delta_label: scn.delta_label, output_label: scn.output_label,
      });
      if (d.enabled) setScnOut(d.scenario);
    } catch (e) { setScnOut({ text: "Couldn't compute the scenario." }); }
  };
  const loadGoals = async () => { try { const d = await call("/ai/board/goals"); if (d.enabled) setGoals(d.goals || {}); } catch (e) { setGoals({}); } };
  const saveGoal = async () => {
    if (!goalForm.metric_key || goalForm.target === "") return;
    try { const d = await call("/ai/board/goals", "PUT", { metric_key: goalForm.metric_key.trim(), target: parseFloat(goalForm.target) }); if (d.enabled) { setGoals(d.goals || {}); setGoalForm({ metric_key: "", target: "" }); } } catch (e) { /* ignore */ }
  };

  const inp = { fontSize: 12, padding: "6px 8px", borderRadius: 8, border: "1px solid #e2e8f0", width: "100%" };
  return (
    <div style={{ ...card }}>
      <button onClick={() => { const n = !open; setOpen(n); if (n && goals === null) loadGoals(); }}
        style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#4f46e5", padding: 0 }}>
        {open ? "▾" : "▸"} Scenario & goals
      </button>
      {open && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 12 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: 6 }}>What-if (labeled projection)</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <input style={inp} placeholder="ratio (e.g. 3.8)" value={scn.ratio} onChange={e => setScn({ ...scn, ratio: e.target.value })} />
              <input style={inp} placeholder="ratio label (e.g. ROAS)" value={scn.ratio_label} onChange={e => setScn({ ...scn, ratio_label: e.target.value })} />
              <input style={inp} placeholder="change amount (e.g. 10000)" value={scn.delta} onChange={e => setScn({ ...scn, delta: e.target.value })} />
              <button onClick={runScenario} style={{ fontSize: 12, padding: "6px 10px", borderRadius: 8, border: "1px solid #e0e7ff", background: "#eef2ff", color: "#4f46e5", cursor: "pointer", fontWeight: 600 }}>Project</button>
              {scnOut && <div style={{ fontSize: 12, color: "#1e293b", background: "#f8fafc", borderRadius: 8, padding: 8 }}>{scnOut.text} <span style={{ color: "#94a3b8" }}>(projection)</span></div>}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: 6 }}>Goals / targets</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {goals && Object.keys(goals).length > 0 && (
                <div style={{ fontSize: 12, color: "#475569" }}>{Object.entries(goals).map(([k, v]) => <div key={k}>{k}: <b>{v}</b></div>)}</div>
              )}
              <input style={inp} placeholder="metric (e.g. roas)" value={goalForm.metric_key} onChange={e => setGoalForm({ ...goalForm, metric_key: e.target.value })} />
              <input style={inp} placeholder="target (e.g. 4)" value={goalForm.target} onChange={e => setGoalForm({ ...goalForm, target: e.target.value })} />
              <button onClick={saveGoal} style={{ fontSize: 12, padding: "6px 10px", borderRadius: 8, border: "1px solid #e0e7ff", background: "#eef2ff", color: "#4f46e5", cursor: "pointer", fontWeight: 600 }}>Save target</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Brand colors editor (R22) — image extraction (client-side) + manual pick ──────────────
function BrandColorsPanel({ theme, defaultPalette, onSave, onReset, onClose }) {
  // Start from the SAVED brand palette when one is enabled, else EMPTY (never pre-fill the
  // Boomerang defaults — that mixed defaults with extracted colors and made charts use the
  // defaults first). Empty + Save-disabled ⇒ the only way to "use defaults" is Reset.
  const startPal = (theme.enabled && theme.palette && theme.palette.length ? theme.palette : []).slice(0, 12);
  const [pal, setPal] = useState(startPal);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const fileRef = useRef(null);

  const up = (c) => (c || "").toUpperCase();
  const addColor = (hex) => setPal((p) => (p.includes(up(hex)) ? p : [...p, up(hex)].slice(0, 12)));
  const removeAt = (i) => setPal((p) => p.filter((_, idx) => idx !== i));
  const setAt = (i, hex) => setPal((p) => p.map((c, idx) => (idx === i ? up(hex) : c)));

  const onImage = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setBusy(true); setNote("Reading image…");
    try {
      const colors = await extractPaletteFromImage(f, 6);
      if (colors.length) {
        // REPLACE with the extracted colors so the palette is unambiguously "from your image"
        // (append mixed them with whatever was pre-filled). Add/remove after to fine-tune.
        setPal(colors.slice(0, 12));
        setNote(`These ${colors.length} color${colors.length !== 1 ? "s were" : " was"} pulled from your image — they're now your brand palette. Add or remove any, then Save.`);
      } else setNote("Couldn't read colors from that image — add them by hand instead.");
    } catch (err) { setNote("Couldn't read that image — add colors by hand instead."); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => { setBusy(true); await onSave(pal, { enabled: true, source: "manual" }); setBusy(false); onClose(); };
  const reset = async () => { setBusy(true); await onReset(); setBusy(false); onClose(); };

  return (
    <div style={{ ...card, borderColor: "#e0e7ff" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>🎨 Brand colors</span>
        <button onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", color: "#94a3b8", fontSize: 18, lineHeight: 1 }}>×</button>
      </div>
      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12, lineHeight: 1.5 }}>
        Used for the chart series here and in the exported PowerPoint. Upload a graph image to pull its colors, or add them by hand. Positive/negative status colors stay fixed for readability.
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        {pal.map((c, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 4, border: "1px solid #e2e8f0", borderRadius: 8, padding: "3px 6px" }}>
            <input type="color" value={c} onChange={(e) => setAt(i, e.target.value)} style={{ width: 22, height: 22, border: "none", background: "none", padding: 0, cursor: "pointer" }} />
            <span style={{ fontSize: 11, color: "#475569", fontFamily: "monospace" }}>{c}</span>
            <button onClick={() => removeAt(i)} title="Remove" style={{ border: "none", background: "none", cursor: "pointer", color: "#cbd5e1", fontSize: 14, lineHeight: 1 }}>×</button>
          </div>
        ))}
        {pal.length === 0 && <span style={{ fontSize: 12, color: "#94a3b8" }}>No brand colors yet — upload a graph image to pull them, or add by hand. (Empty = the Boomerang default palette.)</span>}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <label style={{ fontSize: 12, padding: "6px 10px", borderRadius: 8, border: "1px dashed #c7d2fe", color: "#4f46e5", cursor: "pointer", fontWeight: 600 }}>
          + Upload graph image
          <input ref={fileRef} type="file" accept="image/*" onChange={onImage} style={{ display: "none" }} />
        </label>
        <label style={{ fontSize: 12, padding: "6px 10px", borderRadius: 8, border: "1px solid #e2e8f0", color: "#334155", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
          + Add color
          <input type="color" onChange={(e) => addColor(e.target.value)} style={{ width: 20, height: 20, border: "none", background: "none", padding: 0, cursor: "pointer" }} />
        </label>
        <button onClick={() => setPal(defaultPalette.slice(0, 8))} style={{ fontSize: 12, padding: "6px 10px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", cursor: "pointer" }} title="Fill the editor with the Boomerang palette so you can tweak it">Start from Boomerang</button>
        <div style={{ flex: 1 }} />
        <button disabled={busy} onClick={reset} style={{ fontSize: 12, padding: "6px 12px", borderRadius: 999, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", cursor: busy ? "default" : "pointer", fontWeight: 600 }}>Reset to Boomerang</button>
        <button disabled={busy || pal.length === 0} onClick={save} style={{ fontSize: 12, padding: "6px 14px", borderRadius: 999, border: "none", background: (busy || pal.length === 0) ? "#c7d2fe" : "#4f46e5", color: "#fff", cursor: (busy || pal.length === 0) ? "default" : "pointer", fontWeight: 700 }}>{busy ? "Saving…" : "Save brand colors"}</button>
      </div>
      {note && <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 8 }}>{note}</div>}
    </div>
  );
}
